import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../../lib/api';
import { formatCurrency, formatDate, formatCommissionRate } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Progress } from '../../components/ui/progress';
import { Separator } from '../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
  User, Building, DollarSign, Percent, Upload, Send, Save, Calculator,
  Users, ArrowRight, FileText, CheckCircle, AlertCircle, MessageSquare,
  FileUp, X, Info, CreditCard, Landmark, Calendar, Phone, Mail, Hash,
  Home, Receipt, Banknote, CheckCheck, Clock, ChevronLeft, ChevronRight,
  Shield, Briefcase, UserCheck, MapPin, Zap, Search, Plus, Trash2, UserPlus,
} from 'lucide-react';

const DOCUMENT_TYPES = [
  { key: 'signed_bank_lo', label: 'Bank Letter of Offer', required: true, icon: Landmark },
  { key: 'signed_spa', label: 'Signed SPA', required: true, icon: FileText },
  { key: 'sales_form', label: 'Sales Form', required: true, icon: Receipt },
  { key: 'booking_fee_slip', label: 'Booking Fee Slip', required: false, icon: CreditCard },
  { key: 'downpayment_slip', label: 'Downpayment Slip', required: false, icon: Banknote },
  { key: 'seal', label: 'Company Seal/Stamp', required: false, icon: CheckCheck },
  { key: 'others', label: 'Other Documents', required: false, icon: FileUp },
];

const STEPS = [
  { id: 1, label: 'Entity & Booking', icon: Building },
  { id: 2, label: 'Property & Transaction', icon: Home },
  { id: 3, label: 'Commission Split', icon: DollarSign },
  { id: 4, label: 'Documents', icon: Upload },
  { id: 5, label: 'Declaration', icon: Shield },
];

const RANK_COLORS = {
  evp: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', badge: 'bg-red-600' },
  svp: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', badge: 'bg-amber-600' },
  avp: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', badge: 'bg-blue-600' },
  rm: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200', badge: 'bg-purple-600' },
  neg: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200', badge: 'bg-emerald-600' },
  pic: { bg: 'bg-sky-100', text: 'text-sky-700', border: 'border-sky-200', badge: 'bg-sky-600' },
  trip: { bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-violet-200', badge: 'bg-violet-600' },
  sp_sam: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200', badge: 'bg-slate-700' },
  customer: { bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-200', badge: 'bg-teal-600' },
  introducer: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200', badge: 'bg-orange-600' },
  upline: { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-200', badge: 'bg-indigo-600' },
};

// Default rates per the spec
const SAM_RUMAH_DEFAULT_RATES = {
  upline_1: 14, upline_2: 6, upline_3: 3, upline_4: 2,
  introducer: 65, customer: 5,
};
const SP_SAM_DEFAULT_RATES = {
  neg: 60, rm: 5, avp: 5, svp: 5, evp: 5, pic: 7, trip: 3, sp_sam: 10,
};

// Always render these 5 slots in this exact order, regardless of the agent's actual upline chain.
// Business rule: when e.g. an AVP closes the deal, they can put themselves into the AVP + RM + NEG
// slots so the full hierarchy commission flows back to them.
const SP_SAM_RANK_SLOTS = [
  { rank: 'neg', label: 'NEG 1', color: 'emerald' },
  { rank: 'rm',  label: 'RM 1',  color: 'blue' },
  { rank: 'avp', label: 'AVP 1', color: 'indigo' },
  { rank: 'svp', label: 'SVP 1', color: 'purple' },
  { rank: 'evp', label: 'EVP 1', color: 'rose' },
];

const buildFiveSlots = (existing = []) => {
  return SP_SAM_RANK_SLOTS.map(({ rank, label }) => {
    const found = (existing || []).find(m => (m.rank || '').toLowerCase() === rank);
    if (found) {
      return {
        ...found,
        rank,
        rank_display: found.rank_display || label,
        rate_percentage: found.rate_percentage ?? SP_SAM_DEFAULT_RATES[rank] ?? 0,
      };
    }
    return {
      user_id: '', name: '', agent_code: '', user_code: '', email: '',
      rank,
      rank_display: label,
      role: 'agent',
      rate_percentage: SP_SAM_DEFAULT_RATES[rank] || 0,
      gross_comm: 0, sst_amount: 0, after_sst: 0, platform_fee: 0, nett_comm: 0,
    };
  });
};

const CommissionClaimForm = ({ bookingId, existingClaim, onSuccess, onCancel }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [commissionConfig, setCommissionConfig] = useState(null);
  const [claimId, setClaimId] = useState(null);
  const [revisionComments, setRevisionComments] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [declarations, setDeclarations] = useState({ accurate: false, commission: false, documents: false });
  const [picUsers, setPicUsers] = useState([]);
  // V4.20 — Property-derived multi-PIC assignments (list of { user_id,
  // full_name, tier, split_mode, split_value }). Rendered as one row per
  // PIC in Step 3 so multi-PIC projects show all PICs with their configured
  // split, not just a single dropdown.
  const [picAssignments, setPicAssignments] = useState([]);
  // V4.20 — Property-derived rates for SAM Rumah recipient defaults so that
  // when the closer selects the customer, we can auto-populate Customer
  // Cashback and Introducer Commission with the property's configured
  // values instead of the hard-coded SAM_RUMAH_DEFAULT_RATES fallbacks.
  const [propertyRates, setPropertyRates] = useState({
    customer_cashback: SAM_RUMAH_DEFAULT_RATES.customer,
    introducer: SAM_RUMAH_DEFAULT_RATES.introducer,
  });
  const fileInputRefs = useRef({});

  // SAM Rumah distribution (customer hierarchy: Upline 1-4, Introducer, Customer)
  const [samRumahChain, setSamRumahChain] = useState([]);
  // SP SAM distribution (agent hierarchy: NEG / RM / AVP / SVP / EVP) — always 5 fixed slots
  const [spSamChain, setSpSamChain] = useState(() => buildFiveSlots([]));
  // Per-slot agent search popover state
  const [spSlotSearchOpen, setSpSlotSearchOpen] = useState(null); // index or null
  const [spSlotSearchQuery, setSpSlotSearchQuery] = useState('');
  const [spSlotSearchResults, setSpSlotSearchResults] = useState([]);

  // Manual SAM Rumah recipient entry
  const [srSearchQuery, setSrSearchQuery] = useState('');
  const [srSearchResults, setSrSearchResults] = useState([]);
  const [srSearching, setSrSearching] = useState(false);
  const srSearchTimeout = useRef(null);

  const [formData, setFormData] = useState({
    entity: 'sam_rumah',
    booking_id: bookingId || '',
    customer_full_name: '',
    customer_contact_no: '',
    customer_email: '',
    customer_nric: '',
    booking_date: '',
    signing_date: '',
    project_name: '',
    lot_status: 'non_bumi',
    unit_number: '',
    spa_price: 0,
    nett_price: 0,
    bank_name: '',
    loan_amount: 0,
    loan_type: 'conventional',
    commission_percentage: 0,
    referral_fee_percentage: 0,
    status_checklist: [],
    full_loan: false,
    cash_out: 0,
    downpayment: 0,
    remark: '',
    // SP SAM specific fields
    pic_user_id: '',
    pic_user_name: '',
    pic_rate: 7,
    trip_rate: 3,
    sp_sam_profit_rate: 10,
    // Fast Comm
    fast_comm: '0',
    fast_comm_fee_rate: 16.67,
    // V4.6: payout destination — "wallet" (default) or "direct_bank"
    fast_comm_payout_method: 'wallet',
    // SST
    sst_percentage: 8,
    // Platform fee rate (applied per recipient)
    platform_fee_percentage: 5,
  });

  const isEditing = !!existingClaim;
  const isRevision = existingClaim?.status === 'revision_requested';
  const isSamRumah = formData.entity === 'sam_rumah';
  // V4.20 — Fixed-mode detection MUST NOT gate on `commission_fixed_amount > 0`.
  // A newly-loaded Fixed property might momentarily land in state before the
  // amount syncs; requiring `> 0` would flip the UI back to percentage input
  // and hide the Fixed RM field. Show the Fixed input whenever mode is fixed
  // and let the user (or auto-populate) fill in the amount.
  const isFixedCommission = formData.commission_mode === 'fixed';

  // === POOL CALCULATIONS ===
  // V4.18.3 — Support FIXED commission. When the schedule resolves to a
  // fixed RM amount (e.g. "RM 10,000 on/after 17 June"), `grossCommission`
  // is that flat number instead of `nett_price × commission_percentage`.
  // Everything downstream (referral fees, net commission, SAM pool, SP SAM
  // pool, recipient splits) keeps using the same formulas — they just
  // operate on a different gross.
  const grossCommission = isFixedCommission
    ? Number(formData.commission_fixed_amount || 0)
    : formData.nett_price * (formData.commission_percentage / 100);
  const referralFees = grossCommission * (formData.referral_fee_percentage / 100);
  const netCommission = grossCommission - referralFees;
  const samRumahPool = isSamRumah ? (netCommission * 0.6 / 2) : 0;
  const spSamPool = isSamRumah ? (netCommission - samRumahPool) : netCommission;

  // === RECIPIENT CALCULATION FUNCTIONS ===
  const calcSamRumahRecipient = useCallback((ratePct) => {
    const gross = samRumahPool * ((ratePct || 0) / 100);
    const sst = gross * (formData.sst_percentage / 100);
    const afterSst = gross - sst;
    const platformFee = afterSst * (formData.platform_fee_percentage / 100);
    const nett = afterSst - platformFee;
    return { gross_comm: gross, sst_amount: sst, after_sst: afterSst, platform_fee: platformFee, nett_comm: nett };
  }, [samRumahPool, formData.sst_percentage, formData.platform_fee_percentage]);

  const calcSpSamRecipient = useCallback((ratePct, hasPlatformFee = true) => {
    const gross = spSamPool * ((ratePct || 0) / 100);
    const sst = gross * (formData.sst_percentage / 100);
    const afterSst = gross - sst;
    const platformFee = hasPlatformFee ? afterSst * (formData.platform_fee_percentage / 100) : 0;
    const nett = afterSst - platformFee;
    return { gross_comm: gross, sst_amount: sst, after_sst: afterSst, platform_fee: platformFee, nett_comm: nett };
  }, [spSamPool, formData.sst_percentage, formData.platform_fee_percentage]);

  const calcSpSamProfit = useCallback((ratePct) => {
    const gross = spSamPool * ((ratePct || 0) / 100);
    return { gross_comm: gross, sst_amount: 0, after_sst: gross, platform_fee: 0, nett_comm: gross };
  }, [spSamPool]);

  // Load existing claim
  useEffect(() => {
    if (existingClaim) {
      setClaimId(existingClaim.id);
      // Defensive normalisers — legacy claims (seeded data, older write paths)
      // may carry `status_checklist` / `documents` / recipient lists in the
      // wrong shape (e.g. `{}` instead of `[]`, or a string). Trying to call
      // array methods on them later (Step 4: `.includes`, `.filter`) crashed
      // the form. Coerce to the expected shape up-front.
      const toArr = (v) => (Array.isArray(v) ? v : []);
      const toDocsMap = (v) => {
        if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
        const out = {};
        Object.keys(v).forEach((k) => {
          const val = v[k];
          out[k] = Array.isArray(val) ? val : (val ? [val] : []);
        });
        return out;
      };
      setFormData(prev => ({
        ...prev,
        entity: existingClaim.entity || 'sam_rumah',
        booking_id: existingClaim.booking_id || '',
        customer_full_name: existingClaim.customer_full_name || '',
        customer_contact_no: existingClaim.customer_contact_no || '',
        customer_email: existingClaim.customer_email || '',
        customer_nric: existingClaim.customer_nric || '',
        booking_date: existingClaim.booking_date || '',
        signing_date: existingClaim.signing_date || '',
        project_name: existingClaim.project_name || '',
        lot_status: existingClaim.lot_status || 'non_bumi',
        unit_number: existingClaim.unit_number || '',
        spa_price: existingClaim.spa_price || 0,
        nett_price: existingClaim.nett_price || 0,
        bank_name: existingClaim.bank_name || '',
        loan_amount: existingClaim.loan_amount || 0,
        loan_type: existingClaim.loan_type || 'conventional',
        commission_percentage: existingClaim.commission_percentage || 0,
        // V4.18.3 — restore fixed-mode markers so re-opening the form shows
        // "RM 10,000 (Fixed)" instead of falling back to the 3% input.
        commission_mode: existingClaim.commission_mode || 'percentage',
        commission_fixed_amount: existingClaim.commission_fixed_amount || 0,
        referral_fee_percentage: existingClaim.referral_fee_percentage || 0,
        status_checklist: toArr(existingClaim.status_checklist),
        full_loan: existingClaim.full_loan || false,
        cash_out: existingClaim.cash_out || 0,
        downpayment: existingClaim.downpayment || 0,
        remark: existingClaim.remark || '',
        pic_user_id: existingClaim.pic_user_id || '',
        pic_user_name: existingClaim.pic_user_name || '',
        pic_rate: existingClaim.pic_rate || 7,
        trip_rate: existingClaim.trip_rate || 3,
        sp_sam_profit_rate: existingClaim.sp_sam_profit_rate || 10,
        fast_comm: existingClaim.fast_comm || '0',
        fast_comm_fee_rate: existingClaim.fast_comm_fee_rate || 16.67,
        fast_comm_payout_method: existingClaim.fast_comm_payout_method || 'wallet',
        sst_percentage: existingClaim.sst_percentage || 8,
        platform_fee_percentage: existingClaim.platform_fee_percentage || 5,
      }));
      if (toArr(existingClaim.sam_rumah_recipients).length > 0) setSamRumahChain(existingClaim.sam_rumah_recipients);
      if (toArr(existingClaim.sp_sam_recipients).length > 0) {
        setSpSamChain(buildFiveSlots(existingClaim.sp_sam_recipients));
      } else if (existingClaim.agent_id) {
        loadAgentChain(existingClaim.agent_id);
      } else {
        setSpSamChain(buildFiveSlots([]));
      }
      setUploadedDocs(toDocsMap(existingClaim.documents));
      if (toArr(existingClaim.revision_requests).length > 0) setRevisionComments(existingClaim.revision_requests);
    }
  }, [existingClaim]);

  useEffect(() => { loadBookings(); loadPicUsers(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // For a new claim, preload the hierarchy for the logged-in user (so NEG row is ready immediately).
    // Subsequent booking selection will refresh it to the booking's real agent.
    if (user?.id && !isEditing) loadAgentChain(user.id);
  }, [user?.id, isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBookings = async () => {
    try {
      // Task 4 (V4.18): server-side filter — exclude bookings that already
      // have ANY commission claim (regardless of status) so an agent can't
      // create a duplicate claim. Still apply the spa_signed/completed status
      // gate client-side because only those bookings are claimable.
      const res = await api.get('/bookings', { params: { exclude_claimed: true, limit: 500 } });
      const list = Array.isArray(res.data) ? res.data : (res.data?.items || []);
      setBookings(list.filter(b => ['spa_signed', 'completed'].includes(b.status)));
    } catch (error) { console.error('Error loading bookings:', error); }
  };

  const loadPicUsers = async () => {
    try {
      const res = await api.get('/commission-claims/helpers/pic-users');
      setPicUsers(Array.isArray(res.data) ? res.data : []);
    } catch (error) { console.error('Error loading PIC users:', error); }
  };

  const loadAgentChain = async (agentId) => {
    try {
      const res = await api.get(`/commission-claims/helpers/agent-upline/${agentId}`);
      const chain = res.data.upline_chain || [];
      // Overlay chain data onto the 5 fixed rank slots; slots without a matching member stay empty + editable.
      setSpSamChain(buildFiveSlots(chain.map(m => ({
        ...m,
        rate_percentage: m.rate_percentage || SP_SAM_DEFAULT_RATES[m.rank?.toLowerCase()] || 0,
        gross_comm: 0, sst_amount: 0, after_sst: 0, platform_fee: 0, nett_comm: 0,
      }))));
    } catch (error) {
      console.error('Error loading agent chain:', error);
      setSpSamChain(buildFiveSlots([]));
    }
  };

  const loadCustomerChain = async (customerId, ratesOverride) => {
    try {
      const res = await api.get(`/commission-claims/helpers/cobroke-chain/${customerId}`);
      const chain = res.data.cobroke_chain || [];
      // Map to SAM Rumah distribution with default rates
      // V4.20 — for Customer/Introducer, prefer the PROPERTY-configured
      // customer_cashback_percentage / introducer_commission_percentage
      // over the hard-coded SAM_RUMAH_DEFAULT_RATES fallbacks so the claim
      // stays in sync with property config. `ratesOverride` bypasses the
      // stale-closure trap when called immediately after `setPropertyRates`
      // in the same event-loop tick (React hasn't re-rendered yet).
      const rates = ratesOverride || propertyRates;
      setSamRumahChain(chain.map(member => {
        const roleKey = member.level?.toLowerCase() || member.rank?.toLowerCase() || '';
        const defaultRate =
          roleKey.includes('upline_1') || roleKey.includes('upline1') ? SAM_RUMAH_DEFAULT_RATES.upline_1 :
          roleKey.includes('upline_2') || roleKey.includes('upline2') ? SAM_RUMAH_DEFAULT_RATES.upline_2 :
          roleKey.includes('upline_3') || roleKey.includes('upline3') ? SAM_RUMAH_DEFAULT_RATES.upline_3 :
          roleKey.includes('upline_4') || roleKey.includes('upline4') ? SAM_RUMAH_DEFAULT_RATES.upline_4 :
          roleKey.includes('introducer') ? rates.introducer :
          roleKey.includes('customer') ? rates.customer_cashback : 0;
        return {
          ...member,
          rate_percentage: member.rate_percentage || defaultRate,
          gross_comm: 0, sst_amount: 0, after_sst: 0, platform_fee: 0, nett_comm: 0,
        };
      }));
    } catch (error) { setSamRumahChain([]); }
  };

  const handleBookingSelect = async (bId) => {
    const booking = bookings.find(b => b.id === bId);
    if (!booking) return;
    setSelectedBooking(booking);
    const propertyName = booking.property?.name || booking.property_name || '';
    const unitNumber = booking.unit?.unit_number || booking.manual_unit_number || booking.unit_number || '';
    const unitPrice = booking.unit?.price || booking.unit_price || booking.booking_fee_amount || 0;
    // V4.20 — Step 2 Task 1: Net Price = Unit Price - Loan Amount. Loan
    // amount is captured on the booking's dynamic form_data during booking
    // creation (see /bookings/with-form). Falls back to 0 for legacy
    // bookings that didn't collect a loan figure.
    const loanAmount = Number(
      booking.form_data?.loan_amount
      ?? booking.loan_amount
      ?? 0
    ) || 0;
    const netPrice = Math.max(0, Number(unitPrice) - loanAmount);

    // === V4.10.3: Always lock commission rate from agent edits ===
    // Resolution priority:
    //   1. booking.commission_snapshot — immutable, captured at booking creation
    //   2. property.commission_schedules resolved against booking.created_at (live for legacy bookings)
    //   3. property.commission_percentage (base)
    const snap = booking.commission_snapshot || null;

    // Fetch property commission-config (also returns schedules + auxiliary rates)
    let cfg = null;
    if (booking.property_id || booking.property?.id) {
      const propId = booking.property_id || booking.property?.id;
      try {
        const res = await api.get(`/properties/${propId}/commission-config`);
        cfg = res.data || null;
      } catch (err) {
        console.warn('Failed to load property commission-config — falling back to defaults', err);
      }
    }

    // V4.20 — Referral fee resolves from (1) property.referral_fee_percentage
    // returned on the booking's property blob, else (2) commission-config
    // response (V4.20 exposes it there too), else 0.
    const referralPct = Number(
      booking.property?.referral_fee_percentage
      ?? cfg?.referral_fee_percentage
      ?? 0
    ) || 0;
    // V4.20 — Customer Cashback and Introducer Commission come from the
    // property config too (see /properties/{id}/commission-config). Used
    // below to pre-populate the SAM Rumah Distribution recipient rates.
    const customerCashbackPct = Number(
      booking.property?.customer_cashback_percentage
      ?? cfg?.customer_cashback_percentage
      ?? SAM_RUMAH_DEFAULT_RATES.customer
    ) || 0;
    const introducerPct = Number(
      booking.property?.introducer_commission_percentage
      ?? cfg?.introducer_commission_percentage
      ?? SAM_RUMAH_DEFAULT_RATES.introducer
    ) || 0;
    setPropertyRates({ customer_cashback: customerCashbackPct, introducer: introducerPct });

    // Client-side resolver mirroring backend resolve_commission_for_date.
    const resolveForDate = (schedules, baseMode, basePct, baseFixed, bookingDate) => {
      const bd = (bookingDate || '').slice(0, 10);
      if (!bd) return { mode: baseMode, pct: basePct, fixed: baseFixed, source: 'base', effective_from: null };
      const parsed = (schedules || [])
        .filter(s => s.effective_from)
        .map(s => ({ ef: String(s.effective_from).slice(0, 10), s }))
        .sort((a, b) => b.ef.localeCompare(a.ef));
      for (const { ef, s } of parsed) {
        if (ef <= bd) {
          return {
            mode: s.commission_mode || baseMode,
            pct: s.commission_mode === 'fixed' ? 0 : (s.commission_percentage ?? basePct),
            fixed: s.commission_mode === 'fixed' ? (s.commission_fixed_amount ?? 0) : 0,
            source: 'schedule',
            effective_from: ef,
            note: s.note,
          };
        }
      }
      return { mode: baseMode, pct: basePct, fixed: baseFixed, source: 'base', effective_from: null };
    };

    let commissionPct, commissionMode, commissionFixed;
    let rateSource, rateEffectiveFrom, rateNote;
    if (snap) {
      commissionMode = snap.commission_mode || 'percentage';
      commissionPct = snap.commission_percentage ?? 3;
      commissionFixed = snap.commission_fixed_amount ?? 0;
      rateSource = 'snapshot';
      rateEffectiveFrom = snap.effective_from;
      rateNote = snap.schedule_note;
    } else if (cfg) {
      const r = resolveForDate(
        cfg.commission_schedules || [],
        cfg.commission_mode || 'percentage',
        cfg.commission_percentage ?? 3,
        cfg.commission_fixed_amount ?? 0,
        booking.created_at,
      );
      commissionMode = r.mode;
      commissionPct = r.pct;
      commissionFixed = r.fixed;
      rateSource = r.source;
      rateEffectiveFrom = r.effective_from;
      rateNote = r.note;
    } else {
      commissionMode = 'percentage';
      commissionPct = booking.property?.commission_percentage || booking.commission_percentage || 3;
      commissionFixed = 0;
      rateSource = 'fallback';
    }

    if (cfg) setCommissionConfig({
      ...cfg,
      _booking_snapshot: snap,
      _resolved_rate: { source: rateSource, effective_from: rateEffectiveFrom, note: rateNote, booking_date: booking.created_at },
    });

    // Pre-fill PIC rate from property's pic_commission_percentage (replaces hardcoded 7%)
    const inheritedPicRate = cfg?.pic_commission_percentage ?? 7;
    // Pick a default fast_comm option that's enabled for this project
    const enabledFast = cfg?.fast_comm_enabled || ['0', '50', '100'];
    const defaultFastComm = enabledFast.includes('0') ? '0' : enabledFast[0] || '0';
    // V4.20 — pic_assignments (multi-PIC): expose entire assignment list to
    // the UI so Step 3 can render one row per assigned PIC with the
    // per-PIC split value. Primary PIC becomes the default single-PIC
    // dropdown selection for legacy compatibility.
    const assignments = Array.isArray(cfg?.pic_assignments) ? cfg.pic_assignments : [];
    setPicAssignments(assignments);
    const primaryPic = assignments.find(a => a.tier === 'primary') || assignments[0];

    setFormData(prev => ({
      ...prev,
      booking_id: booking.id,
      customer_full_name: booking.buyer_name || booking.form_data?.buyer_name || '',
      customer_contact_no: booking.buyer_phone || booking.form_data?.buyer_phone || '',
      customer_email: booking.buyer_email || booking.form_data?.buyer_email || '',
      customer_nric: booking.buyer_ic || booking.form_data?.buyer_ic || '',
      project_name: propertyName,
      unit_number: unitNumber,
      spa_price: unitPrice,
      // V4.20 — Net Price = Unit Price - Loan Amount (Task 1). Previously we
      // simply used unit price; now the loan_amount captured at booking
      // creation is deducted so the claim reflects the "real cash" price.
      nett_price: netPrice,
      loan_amount: loanAmount,
      commission_percentage: commissionPct,
      commission_mode: commissionMode,
      commission_fixed_amount: commissionFixed,
      commission_locked_by_snapshot: rateSource !== 'fallback',
      commission_rate_source: rateSource,
      referral_fee_percentage: referralPct,
      booking_date: booking.created_at?.split('T')[0] || '',
      pic_user_id: prev.pic_user_id || booking.pic_user_id || booking.property?.pic_user_id || primaryPic?.user_id || '',
      pic_user_name: prev.pic_user_name || booking.pic_user_name || primaryPic?.full_name || '',
      pic_rate: prev.pic_rate && prev.pic_rate !== 7 ? prev.pic_rate : inheritedPicRate,
      fast_comm: enabledFast.includes(prev.fast_comm) ? prev.fast_comm : defaultFastComm,
    }));
    if (booking.buyer_id && isSamRumah) loadCustomerChain(booking.buyer_id, {
      customer_cashback: customerCashbackPct,
      introducer: introducerPct,
    });
    const chainAgentId = booking.agent_id || booking.agent?.id || user?.id;
    if (chainAgentId) loadAgentChain(chainAgentId);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Recalculate all commissions when pools change
  const recalculateAll = useCallback(() => {
    if (netCommission <= 0) return;

    // Recalc SAM Rumah chain
    if (isSamRumah) {
      setSamRumahChain(prev => prev.map(m => ({
        ...m,
        ...calcSamRumahRecipient(m.rate_percentage),
      })));
    }

    // Recalc SP SAM chain (NEG/RM/AVP/SVP/EVP get platform fee)
    setSpSamChain(prev => prev.map(m => ({
      ...m,
      ...calcSpSamRecipient(m.rate_percentage, true),
    })));
  }, [netCommission, isSamRumah, calcSamRumahRecipient, calcSpSamRecipient]);

  useEffect(() => { recalculateAll(); }, [recalculateAll, formData.nett_price, formData.commission_percentage, formData.commission_mode, formData.commission_fixed_amount, formData.referral_fee_percentage, formData.entity, formData.sst_percentage, formData.platform_fee_percentage]);

  const updateSamRumahRate = (index, field, value) => {
    setSamRumahChain(prev => {
      const updated = [...prev];
      const numValue = parseFloat(value) || 0;
      if (field === 'rate_percentage') {
        updated[index] = { ...updated[index], rate_percentage: numValue, ...calcSamRumahRecipient(numValue) };
      }
      return updated;
    });
  };

  const updateSpSamRate = (index, field, value) => {
    setSpSamChain(prev => {
      const updated = [...prev];
      const numValue = parseFloat(value) || 0;
      if (field === 'rate_percentage') {
        updated[index] = { ...updated[index], rate_percentage: numValue, ...calcSpSamRecipient(numValue, true) };
      }
      return updated;
    });
  };

  // Search any user to assign to a specific SP SAM slot (NEG / RM / AVP / SVP / EVP).
  // Returns agents primarily but accepts any registered user the admin wants.
  const spSlotSearchTimeout = useRef(null);
  const openSpSlotSearch = (slotIndex) => {
    setSpSlotSearchOpen(slotIndex);
    setSpSlotSearchQuery('');
    setSpSlotSearchResults([]);
  };
  const handleSpSlotSearch = (query) => {
    setSpSlotSearchQuery(query);
    if (spSlotSearchTimeout.current) clearTimeout(spSlotSearchTimeout.current);
    if (!query || query.length < 2) { setSpSlotSearchResults([]); return; }
    spSlotSearchTimeout.current = setTimeout(async () => {
      try {
        // V4.20 — Step 3 Task 3: filter SP SAM slot candidates to the
        // closing agent's upline chain (RM/AVP/SVP/EVP). Backend does the
        // filter server-side via `upline_of=<closerId>`.
        const b = bookings.find(x => x.id === formData.booking_id);
        const closerId = b?.agent_id || b?.agent?.id || existingClaim?.agent_id || user?.id || '';
        const params = new URLSearchParams({ q: query });
        if (closerId) params.set('upline_of', closerId);
        const res = await api.get(`/commission-claims/helpers/search-users?${params.toString()}`);
        setSpSlotSearchResults(Array.isArray(res.data) ? res.data : []);
      } catch { setSpSlotSearchResults([]); }
    }, 350);
  };
  const assignSpSlotAgent = (slotIndex, picked) => {
    setSpSamChain(prev => prev.map((m, i) => i === slotIndex ? {
      ...m,
      user_id: picked.id,
      name: picked.full_name || picked.email || 'Unknown',
      agent_code: picked.agent_code || m.agent_code || '',
      user_code: picked.user_code || m.user_code || '',
      email: picked.email || '',
      role: picked.role || 'agent',
    } : m));
    setSpSlotSearchOpen(null);
    setSpSlotSearchQuery('');
    setSpSlotSearchResults([]);
  };
  const clearSpSlotAgent = (slotIndex) => {
    setSpSamChain(prev => prev.map((m, i) => i === slotIndex ? {
      ...m, user_id: '', name: '', agent_code: '', user_code: '', email: '',
    } : m));
  };

  // === MANUAL SAM RUMAH RECIPIENT FUNCTIONS ===
  const SR_ROLE_OPTIONS = [
    { value: 'customer', label: 'Customer', defaultRate: SAM_RUMAH_DEFAULT_RATES.customer },
    { value: 'introducer', label: 'Introducer', defaultRate: SAM_RUMAH_DEFAULT_RATES.introducer },
    { value: 'upline_1', label: 'Upline 1', defaultRate: SAM_RUMAH_DEFAULT_RATES.upline_1 },
    { value: 'upline_2', label: 'Upline 2', defaultRate: SAM_RUMAH_DEFAULT_RATES.upline_2 },
    { value: 'upline_3', label: 'Upline 3', defaultRate: SAM_RUMAH_DEFAULT_RATES.upline_3 },
    { value: 'upline_4', label: 'Upline 4', defaultRate: SAM_RUMAH_DEFAULT_RATES.upline_4 },
  ];

  const handleSrSearch = (query) => {
    setSrSearchQuery(query);
    if (srSearchTimeout.current) clearTimeout(srSearchTimeout.current);
    if (!query || query.length < 2) { setSrSearchResults([]); return; }
    setSrSearching(true);
    srSearchTimeout.current = setTimeout(async () => {
      try {
        const res = await api.get(`/commission-claims/helpers/search-users?q=${encodeURIComponent(query)}`);
        setSrSearchResults(res.data || []);
      } catch { setSrSearchResults([]); }
      finally { setSrSearching(false); }
    }, 400);
  };

  const addSrRecipientFromSearch = (user, role = 'customer') => {
    const roleOption = SR_ROLE_OPTIONS.find(r => r.value === role);
    const defaultRate = roleOption?.defaultRate || 0;
    const calcs = calcSamRumahRecipient(defaultRate);
    setSamRumahChain(prev => [...prev, {
      user_id: user.id,
      name: user.full_name || user.email || 'Unknown',
      agent_code: user.agent_code || '',
      user_code: user.user_code || '',
      email: user.email || '',
      level: role,
      level_display: roleOption?.label || role,
      // V4.18.8 — Backend Pydantic schema requires `rank` + `rank_display`
      // on every recipient. For SAM Rumah the role IS the rank
      // (customer / introducer / upline_1..4).
      rank: role,
      rank_display: roleOption?.label || role,
      role: user.role || 'customer',
      is_agent: user.role === 'agent',
      rate_percentage: defaultRate,
      ...calcs,
    }]);
    setSrSearchQuery('');
    setSrSearchResults([]);
  };

  const loadChainFromUser = async (user) => {
    try {
      const res = await api.get(`/commission-claims/helpers/cobroke-chain/${user.id}`);
      const chain = res.data.cobroke_chain || [];
      if (chain.length === 0) {
        // No chain found — just add as a single customer
        addSrRecipientFromSearch(user, 'customer');
        return;
      }
      setSamRumahChain(chain.map(member => {
        const roleKey = member.level?.toLowerCase() || '';
        const defaultRate =
          roleKey.includes('upline_1') || roleKey.includes('upline1') ? SAM_RUMAH_DEFAULT_RATES.upline_1 :
          roleKey.includes('upline_2') || roleKey.includes('upline2') ? SAM_RUMAH_DEFAULT_RATES.upline_2 :
          roleKey.includes('upline_3') || roleKey.includes('upline3') ? SAM_RUMAH_DEFAULT_RATES.upline_3 :
          roleKey.includes('upline_4') || roleKey.includes('upline4') ? SAM_RUMAH_DEFAULT_RATES.upline_4 :
          roleKey.includes('introducer') ? propertyRates.introducer :
          roleKey.includes('customer') ? propertyRates.customer_cashback : 0;
        return {
          ...member,
          // V4.18.8 — Backend Pydantic schema requires `rank` + `rank_display`
          // on every recipient. Backfill from the SAM-Rumah-specific
          // `level` / `level_display` keys that the cobroke-chain helper
          // historically returned.
          rank: member.rank || member.level || '',
          rank_display: member.rank_display || member.level_display || '',
          rate_percentage: member.rate_percentage || defaultRate,
          gross_comm: 0, sst_amount: 0, after_sst: 0, platform_fee: 0, nett_comm: 0,
        };
      }));
      setSrSearchQuery('');
      setSrSearchResults([]);
    } catch {
      addSrRecipientFromSearch(user, 'customer');
    }
  };

  const addSrManualRecipient = (role = 'customer') => {
    const roleOption = SR_ROLE_OPTIONS.find(r => r.value === role);
    const defaultRate = roleOption?.defaultRate || 0;
    const calcs = calcSamRumahRecipient(defaultRate);
    setSamRumahChain(prev => [...prev, {
      user_id: '',
      name: '',
      agent_code: '',
      user_code: '',
      email: '',
      level: role,
      level_display: roleOption?.label || role,
      // V4.18.8 — Backend Pydantic schema requires `rank` + `rank_display`.
      rank: role,
      rank_display: roleOption?.label || role,
      role: 'customer',
      is_agent: false,
      rate_percentage: defaultRate,
      is_manual: true,
      ...calcs,
    }]);
  };

  const updateSrRecipientName = (index, name) => {
    setSamRumahChain(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], name };
      return updated;
    });
  };

  const updateSrRecipientRole = (index, role) => {
    const roleOption = SR_ROLE_OPTIONS.find(r => r.value === role);
    const defaultRate = roleOption?.defaultRate || 0;
    setSamRumahChain(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        level: role,
        level_display: roleOption?.label || role,
        // V4.18.8 — keep `rank` / `rank_display` aligned with `level`
        // so backend Pydantic validation passes.
        rank: role,
        rank_display: roleOption?.label || role,
        rate_percentage: defaultRate,
        ...calcSamRumahRecipient(defaultRate),
      };
      return updated;
    });
  };

  const removeSrRecipient = (index) => {
    setSamRumahChain(prev => prev.filter((_, i) => i !== index));
  };

  // === TOTALS ===
  // Live-derived chains: recompute gross/SST/platform/net against the current pool + rate on every
  // render. Prevents the "stale 0 after state reset" bug (e.g. when buildFiveSlots / loadCustomerChain
  // overwrites freshly computed values).
  const spSamChainCalc = useMemo(() => spSamChain.map(m => ({
    ...m,
    ...calcSpSamRecipient(m.rate_percentage || 0, true),
  })), [spSamChain, calcSpSamRecipient]);

  const samRumahChainCalc = useMemo(
    () => samRumahChain.map(m => ({
      ...m,
      ...calcSamRumahRecipient(m.rate_percentage || 0),
    })),
    [samRumahChain, calcSamRumahRecipient]
  );

  // SAM Rumah totals
  const srTotalGross = samRumahChainCalc.reduce((s, r) => s + (r.gross_comm || 0), 0);
  const srTotalSST = samRumahChainCalc.reduce((s, r) => s + (r.sst_amount || 0), 0);
  const srTotalPlatform = samRumahChainCalc.reduce((s, r) => s + (r.platform_fee || 0), 0);
  const srTotalNett = samRumahChainCalc.reduce((s, r) => s + (r.nett_comm || 0), 0);

  // SP SAM agent hierarchy totals (uses spSamChainCalc defined above)

  const spAgentTotalGross = spSamChainCalc.reduce((s, r) => s + (r.gross_comm || 0), 0);
  const spAgentTotalSST = spSamChainCalc.reduce((s, r) => s + (r.sst_amount || 0), 0);
  const spAgentTotalPlatform = spSamChainCalc.reduce((s, r) => s + (r.platform_fee || 0), 0);
  const spAgentTotalNett = spSamChainCalc.reduce((s, r) => s + (r.nett_comm || 0), 0);

  // SP SAM PIC, Trip, Profit calculations
  const picCalc = calcSpSamRecipient(formData.pic_rate, false); // PIC: no platform fee
  const tripCalc = calcSpSamRecipient(formData.trip_rate, false); // Trip: no platform fee
  const profitCalc = calcSpSamProfit(formData.sp_sam_profit_rate); // Profit: no SST, no platform fee

  // SP SAM total
  const spTotalGross = spAgentTotalGross + picCalc.gross_comm + tripCalc.gross_comm + profitCalc.gross_comm;
  const spTotalSST = spAgentTotalSST + picCalc.sst_amount + tripCalc.sst_amount;
  const spTotalPlatform = spAgentTotalPlatform;
  const spTotalNett = spAgentTotalNett + picCalc.nett_comm + tripCalc.nett_comm + profitCalc.nett_comm;

  // Fast Comm calculation — applied to the SOLD agent's total net across all slots they occupy.
  // Business rule: the agent who closed the deal gets upfront payout of everything owed to them
  // (regardless of which rank slot[s] they're assigned to — e.g. AVP-closer may fill NEG+RM+AVP
  // with their own user_code and collect the full hierarchy commission upfront).
  const soldAgentId = existingClaim?.agent_id
    || bookings.find(b => b.id === formData.booking_id)?.agent_id
    || user?.id;
  const soldAgentSlots = spSamChainCalc.filter(m => m.user_id && m.user_id === soldAgentId);
  const negSlot = spSamChainCalc.find(m => (m.rank || '').toLowerCase() === 'neg');
  const agentNetAfterPlatform = soldAgentSlots.length > 0
    ? soldAgentSlots.reduce((s, m) => s + (m.nett_comm || 0), 0)
    : (negSlot?.nett_comm || 0);   // fallback: NEG slot's net (default placement for the closer)
  // Fast Comm computation:
  //   "0"   → no advance, agent waits for developer payout
  //   "50"  → agent gets 50% of agent-net upfront, NO charges (Feb 2026 — V4.5)
  //   "100" → agent gets 100% upfront, 16.67% fee + 8% SST applied
  const fastCommOption = formData.fast_comm || '0';
  const isFastComm100 = fastCommOption === '100';
  const isFastComm50 = fastCommOption === '50';
  const isFastComm = isFastComm100 || isFastComm50;
  const fastCommFee = isFastComm100 ? agentNetAfterPlatform * (formData.fast_comm_fee_rate / 100) : 0;
  const fastCommSST = fastCommFee * (formData.sst_percentage / 100);
  const fastCommTotalDeduction = fastCommFee + fastCommSST;
  const agentWalletAmount = isFastComm100
    ? agentNetAfterPlatform - fastCommTotalDeduction
    : isFastComm50
      ? agentNetAfterPlatform * 0.5
      : 0;

  // SP SAM Total Revenue
  const spSamRevenue = profitCalc.nett_comm + spAgentTotalPlatform + (isFastComm100 ? fastCommFee : 0);

  // ═══════════════════════════════════════════════════════════════
  // COMPANY RETAINED PROFIT
  // SAM Rumah retained = unallocated SR pool + platform fees collected from SR recipients
  // SP SAM retained    = unallocated SP pool (after agents/PIC/Trip/profit_rate) + platform fees from SP agents
  // (matches backend calc in /app/backend/routes/commissions.py submit flow)
  // ═══════════════════════════════════════════════════════════════
  const samRumahUnallocated = Math.max(0, samRumahPool - srTotalGross);
  const samRumahRetained = samRumahUnallocated + srTotalPlatform;

  const spSamUnallocated = Math.max(
    0,
    spSamPool - spAgentTotalGross - picCalc.gross_comm - tripCalc.gross_comm - profitCalc.gross_comm
  );
  const spSamRetained = spSamUnallocated + spAgentTotalPlatform;

  // Grand totals
  const grandTotalGross = (isSamRumah ? srTotalGross : 0) + spTotalGross;
  const grandTotalSST = (isSamRumah ? srTotalSST : 0) + spTotalSST;
  const grandTotalPlatform = (isSamRumah ? srTotalPlatform : 0) + spTotalPlatform;
  const grandTotalNett = (isSamRumah ? srTotalNett : 0) + spTotalNett;

  // File upload
  const handleFileUpload = async (docType, file) => {
    if (!claimId) { toast.error('Please save as draft first before uploading documents'); return; }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('document_type', docType);
    setUploadProgress(prev => ({ ...prev, [docType]: 0 }));
    try {
      const res = await api.post(`/commission-claims/${claimId}/upload-document`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setUploadProgress(prev => ({ ...prev, [docType]: Math.round((e.loaded * 100) / e.total) })),
      });
      setUploadedDocs(prev => ({
        ...prev,
        [docType]: [...(prev[docType] || []), { filename: res.data.filename, original_name: file.name, uploaded_at: new Date().toISOString() }],
      }));
      toast.success(`${file.name} uploaded`);
    } catch (error) { toast.error(error.response?.data?.detail || 'Upload failed'); }
    finally { setUploadProgress(prev => ({ ...prev, [docType]: undefined })); }
  };

  const handleFileSelect = (docType) => (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(docType, file);
    e.target.value = '';
  };

  const buildPayload = () => ({
    ...formData,
    sam_rumah_recipients: isSamRumah ? samRumahChainCalc : [],
    // Persist only slots that actually carry a distribution (have a user assigned OR a non-zero rate).
    // All 5 rank slots are rendered in the UI but empty rows should not be sent as recipients.
    sp_sam_recipients: spSamChainCalc.filter(r => r.user_id || (r.rate_percentage && r.rate_percentage > 0)),
    // Pools
    gross_commission: grossCommission,
    referral_fees: referralFees,
    net_commission: netCommission,
    sam_rumah_pool: samRumahPool,
    sp_sam_pool: spSamPool,
    // Totals
    sr_total_platform: srTotalPlatform,
    sp_total_platform: spAgentTotalPlatform,
    sam_rumah_retained: samRumahRetained,
    sp_sam_retained: spSamRetained,
    total_sst_amount: grandTotalSST,
    // SP SAM specific
    pic_commission_amount: picCalc.nett_comm,
    trip_wallet_amount: tripCalc.nett_comm,
    sp_sam_profit_amount: profitCalc.nett_comm,
    // Fast Comm
    fast_comm_fee: fastCommFee,
    fast_comm_sst: fastCommSST,
    fast_comm_total_deduction: fastCommTotalDeduction,
    agent_wallet_amount: agentWalletAmount,
    sp_sam_revenue: spSamRevenue,
    // Legacy fields for backward compat
    management_recipients: spSamChainCalc.filter(r => r.user_id || (r.rate_percentage && r.rate_percentage > 0)),
    cobroke_recipients: isSamRumah ? samRumahChain : [],
  });

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEditing && claimId) {
        await api.put(`/commission-claims/${claimId}`, payload);
        toast.success('Claim updated');
      } else {
        const res = await api.post('/commission-claims', payload);
        setClaimId(res.data.id);
        toast.success('Claim saved as draft');
      }
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleSubmit = async () => {
    if (!formData.booking_id && !isEditing) { toast.error('Please select a booking'); return; }
    if (!formData.customer_full_name) { toast.error('Customer name is required'); return; }
    // V4.18.6 — Fixed-mode claims have commission_percentage = 0 by design.
    // Validate against whichever field drives the gross commission for the
    // selected mode so fixed-rate projects can be submitted.
    if (isFixedCommission) {
      if (!(Number(formData.commission_fixed_amount) > 0)) {
        toast.error('Fixed commission amount is required'); return;
      }
    } else if (!(Number(formData.commission_percentage) > 0)) {
      toast.error('Commission % is required'); return;
    }
    // V4.20 — Step 3 Task 1: Agent Hierarchy is MANDATORY. At minimum the
    // NEG (closer) slot must be filled. Rejecting here prevents claims
    // that would land on the PIC queue with an empty payment tracker.
    const hierarchyFilled = spSamChain.some(m => (m.user_id || '').trim() && Number(m.rate_percentage) > 0);
    if (!hierarchyFilled) {
      toast.error('Agent Hierarchy is required — please assign at least one agent (NEG) before submitting');
      setCurrentStep(3);
      return;
    }
    // V4.20 — Step 3 Task 4: total recipient percentage in each distribution
    // must not exceed 100%. Sum agent + PIC + Trip + Profit for SP SAM,
    // and all Upline/Introducer/Customer rows for SAM Rumah.
    const spSamAgentTotal = spSamChain.reduce((s, m) => s + (Number(m.rate_percentage) || 0), 0);
    const spSamOverheadTotal = (Number(formData.pic_rate) || 0)
      + (Number(formData.trip_rate) || 0)
      + (Number(formData.sp_sam_profit_rate) || 0);
    const spSamTotalPct = spSamAgentTotal + spSamOverheadTotal;
    if (spSamTotalPct > 100.001) {
      toast.error(
        `SP SAM Distribution total ${spSamTotalPct.toFixed(2)}% exceeds 100%. `
        + 'Reduce agent / PIC / Trip / Profit rates.'
      );
      setCurrentStep(3);
      return;
    }
    if (isSamRumah) {
      const srTotalPct = samRumahChain.reduce((s, m) => s + (Number(m.rate_percentage) || 0), 0);
      if (srTotalPct > 100.001) {
        toast.error(
          `SAM Rumah Distribution total ${srTotalPct.toFixed(2)}% exceeds 100%. `
          + 'Reduce recipient rates.'
        );
        setCurrentStep(3);
        return;
      }
    }
    if (!declarations.accurate || !declarations.commission || !declarations.documents) {
      toast.error('Please accept all declarations'); return;
    }
    setLoading(true);
    let submittedOk = false;
    try {
      const payload = buildPayload();
      let targetClaimId = claimId;
      if (isEditing && claimId) {
        await api.put(`/commission-claims/${claimId}`, payload);
      } else {
        const createRes = await api.post('/commission-claims', payload);
        targetClaimId = createRes.data.id;
      }
      await api.post(`/commission-claims/${targetClaimId}/submit`);
      submittedOk = true;
      toast.success(isRevision ? 'Claim resubmitted for review' : 'Claim submitted for review');
    } catch (error) {
      // Task 3 (V4.18): surface the real failure so the user gets a useful
      // message instead of a blank page. Network/Pydantic failures used to
      // bubble out of an inner state update and crash the whole tree.
      console.error('[CommissionClaim:submit] failed', error?.response?.data || error);
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (Array.isArray(detail) ? detail.map(d => d.msg || d.message).filter(Boolean).join(', ') : null)
          || error?.message
          || 'Failed to submit';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
    // Defer parent callback to next tick AND guard with try/catch so any
    // parent render error (e.g. claims list reload failing) does NOT bubble
    // back into this component and white-screen the page.
    if (submittedOk) {
      try {
        if (onSuccess) onSuccess();
      } catch (e) {
        console.error('[CommissionClaim:onSuccess] parent callback failed', e);
        toast.error('Claim submitted, but the page failed to refresh. Please reload.');
      }
    }
  };

  const getStepCompletion = (step) => {
    switch (step) {
      case 1: return formData.entity && formData.booking_id ? 100 : formData.entity ? 50 : 0;
      case 2: {
        // V4.18.6 — Step 2 completion: in fixed-commission mode validate
        // commission_fixed_amount instead of commission_percentage (which is 0
        // by design when the rate is fixed).
        const rateField = isFixedCommission ? 'commission_fixed_amount' : 'commission_percentage';
        const fields = ['customer_full_name', 'customer_nric', 'project_name', 'unit_number', 'nett_price', rateField];
        return Math.round((fields.filter(f => formData[f]).length / fields.length) * 100);
      }
      case 3: return spSamChain.some(m => m.rate_percentage > 0) ? 100 : 0;
      case 4: {
        const requiredDocs = DOCUMENT_TYPES.filter(d => d.required);
        const uploaded = requiredDocs.filter(d => {
          const v = uploadedDocs[d.key];
          return (Array.isArray(v) ? v.length : (v ? 1 : 0)) > 0;
        });
        return requiredDocs.length > 0 ? Math.round((uploaded.length / requiredDocs.length) * 100) : 100;
      }
      case 5: return declarations.accurate && declarations.commission && declarations.documents ? 100 : 0;
      default: return 0;
    }
  };

  const overallCompletion = Math.round(STEPS.reduce((sum, s) => sum + getStepCompletion(s.id), 0) / STEPS.length);
  // Step-position progress: how far the user has actually walked through the wizard.
  // We use this for the top purple bar so it visibly "moves" to the current step,
  // while `overallCompletion` is still available as a data-completeness signal.
  const stepPositionProgress = Math.round((currentStep / STEPS.length) * 100);
  const getRankColor = (rank) => RANK_COLORS[rank?.toLowerCase()] || RANK_COLORS.neg;

  // =================== RENDER ===================
  return (
    <div className="space-y-6" data-testid="commission-claim-form">
      {/* Revision Banner */}
      {isRevision && revisionComments.length > 0 && (
        <Card className="border-orange-300 bg-orange-50" data-testid="revision-banner">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-orange-700">
              <MessageSquare className="h-5 w-5" /> Revision Required
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {revisionComments.map((rev, idx) => (
              <div key={idx} className="p-3 bg-white rounded-lg border border-orange-200">
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className="text-orange-700 border-orange-300">{rev.requested_by_role?.toUpperCase()}</Badge>
                  <span className="text-xs text-gray-500">{formatDate(rev.requested_at)}</span>
                </div>
                {rev.general_comment && <p className="text-sm text-gray-700">{rev.general_comment}</p>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Step Progress */}
      <Card className="bg-gradient-to-r from-slate-50 to-blue-50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">Overall Progress</span>
            <span className="text-sm font-bold text-blue-600" data-testid="progress-pct">{stepPositionProgress}%</span>
          </div>
          <Progress value={stepPositionProgress} className="h-2 mb-4" />
          <div className="flex items-center justify-between" data-testid="step-indicators">
            {STEPS.map((step, idx) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.id;
              const isReached = currentStep >= step.id;
              const isCompleted = getStepCompletion(step.id) === 100;
              return (
                <React.Fragment key={step.id}>
                  <button onClick={() => setCurrentStep(step.id)} className={`flex flex-col items-center gap-1.5 transition-all ${isActive ? 'scale-105' : ''}`} data-testid={`step-${step.id}-btn`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${isActive ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : isCompleted ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                      {isCompleted && !isActive ? <CheckCircle className="h-5 w-5" /> : <StepIcon className="h-5 w-5" />}
                    </div>
                    <span className={`text-xs font-medium ${isActive ? 'text-blue-600' : 'text-gray-500'}`}>{step.label}</span>
                  </button>
                  {idx < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 ${currentStep > step.id ? 'bg-emerald-400' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* ====== STEP 1: Entity & Booking ====== */}
      {currentStep === 1 && (
        <div className="space-y-4" data-testid="step-1-content">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Briefcase className="h-5 w-5" /> Select Entity</CardTitle>
              <CardDescription>Choose the business entity for this commission claim</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <button onClick={() => handleInputChange('entity', 'sam_rumah')} className={`p-6 rounded-xl border-2 transition-all text-left ${formData.entity === 'sam_rumah' ? 'border-emerald-500 bg-emerald-50 shadow-lg shadow-emerald-100' : 'border-gray-200 hover:border-gray-300 bg-white'}`} data-testid="entity-sam-rumah">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg ${formData.entity === 'sam_rumah' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500'}`}>SR</div>
                    <div>
                      <p className="font-semibold text-base">SAM Rumah</p>
                      <p className="text-xs text-gray-500">Full Platform Sale</p>
                    </div>
                  </div>
                  <div className="space-y-1 text-xs text-gray-600">
                    <p>&#x2022; SAM Rumah Pool (60%/2) + SP SAM Pool</p>
                    <p>&#x2022; Customer hierarchy + Agent hierarchy</p>
                    <p>&#x2022; Fast Comm available for Agent</p>
                  </div>
                </button>
                <button onClick={() => handleInputChange('entity', 'sp_sam')} className={`p-6 rounded-xl border-2 transition-all text-left ${formData.entity === 'sp_sam' ? 'border-blue-500 bg-blue-50 shadow-lg shadow-blue-100' : 'border-gray-200 hover:border-gray-300 bg-white'}`} data-testid="entity-sp-sam">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg ${formData.entity === 'sp_sam' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-500'}`}>SP</div>
                    <div>
                      <p className="font-semibold text-base">SP SAM Property</p>
                      <p className="text-xs text-gray-500">Direct Agency Sale</p>
                    </div>
                  </div>
                  <div className="space-y-1 text-xs text-gray-600">
                    <p>&#x2022; SP SAM Pool only (full commission)</p>
                    <p>&#x2022; Agent hierarchy + PIC + Trip</p>
                    <p>&#x2022; SP SAM 10% Profit</p>
                  </div>
                </button>
              </div>
            </CardContent>
          </Card>

          {!isEditing && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2"><FileText className="h-5 w-5" /> Select Booking</CardTitle>
              </CardHeader>
              <CardContent>
                <Select value={formData.booking_id} onValueChange={handleBookingSelect}>
                  <SelectTrigger data-testid="booking-select"><SelectValue placeholder="Select a booking..." /></SelectTrigger>
                  <SelectContent>
                    {bookings.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">No claimable bookings found</div>
                    ) : bookings.map(booking => {
                      const propName = booking.property?.name || booking.property_name || 'Property N/A';
                      const unitNo = booking.unit?.unit_number || booking.manual_unit_number || booking.unit_number || '';
                      const buyer = booking.buyer_name || booking.form_data?.buyer_name || 'Unnamed buyer';
                      const ref = booking.booking_number || booking.booking_code || (booking.id ? booking.id.slice(0, 8) : '');
                      const price = booking.spa_price || booking.nett_price || booking.property?.asking_price;
                      const dateStr = booking.created_at ? new Date(booking.created_at).toLocaleDateString() : '';
                      return (
                        <SelectItem key={booking.id} value={booking.id}>
                          <div className="flex items-start gap-2 py-0.5">
                            <Home className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium truncate">{propName}{unitNo ? ` · ${unitNo}` : ''}</span>
                                <Badge variant="outline" className="text-[10px]">{buyer}</Badge>
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {ref && <span>#{ref}</span>}
                                {price ? <span> · {formatCurrency(price)}</span> : null}
                                {dateStr ? <span> · {dateStr}</span> : null}
                              </div>
                            </div>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>
          )}

          {isEditing && existingClaim && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Editing Claim</p>
                    <p className="font-semibold text-blue-700">{existingClaim.claim_ref}</p>
                  </div>
                  <Badge className={isSamRumah ? 'bg-emerald-500' : 'bg-blue-500'}>
                    {isSamRumah ? 'SAM Rumah' : 'SP SAM'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ====== STEP 2: Property & Transaction ====== */}
      {currentStep === 2 && (
        <div className="space-y-4" data-testid="step-2-content">
          <Card>
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><User className="h-5 w-5" /> Buyer Information</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><User className="h-4 w-4 text-gray-400" /> Full Name *</Label>
                <Input data-testid="customer-name-input" value={formData.customer_full_name} onChange={(e) => handleInputChange('customer_full_name', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><Hash className="h-4 w-4 text-gray-400" /> NRIC *</Label>
                <Input data-testid="customer-nric-input" value={formData.customer_nric} onChange={(e) => handleInputChange('customer_nric', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><Phone className="h-4 w-4 text-gray-400" /> Contact *</Label>
                <Input data-testid="customer-phone-input" value={formData.customer_contact_no} onChange={(e) => handleInputChange('customer_contact_no', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><Mail className="h-4 w-4 text-gray-400" /> Email *</Label>
                <Input data-testid="customer-email-input" type="email" value={formData.customer_email} onChange={(e) => handleInputChange('customer_email', e.target.value)} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Building className="h-5 w-5" /> Property & Financial Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2"><Label>Project Name *</Label><Input data-testid="project-name-input" value={formData.project_name} onChange={(e) => handleInputChange('project_name', e.target.value)} /></div>
                <div className="space-y-2"><Label>Unit Number *</Label><Input data-testid="unit-number-input" value={formData.unit_number} onChange={(e) => handleInputChange('unit_number', e.target.value)} /></div>
                <div className="space-y-2">
                  <Label>Lot Status *</Label>
                  <Select value={formData.lot_status} onValueChange={(v) => handleInputChange('lot_status', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="bumi">Bumi</SelectItem><SelectItem value="non_bumi">Non-Bumi</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <Separator />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2"><Label>SPA Price (RM)</Label><Input type="number" value={formData.spa_price || ''} onChange={(e) => handleInputChange('spa_price', parseFloat(e.target.value) || 0)} /></div>
                <div className="space-y-2"><Label>Net Price (RM) *</Label><Input data-testid="nett-price-input" type="number" value={formData.nett_price || ''} onChange={(e) => handleInputChange('nett_price', parseFloat(e.target.value) || 0)} /></div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5">
                    {isFixedCommission ? 'Commission Rate (Fixed RM) *' : 'Commission Rate (%) *'}
                    {formData.commission_locked_by_snapshot && (
                      <span
                        className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border ${
                          formData.commission_rate_source === 'snapshot'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : formData.commission_rate_source === 'schedule'
                              ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                              : 'bg-gray-50 text-gray-600 border-gray-200'
                        }`}
                        data-testid="commission-snapshot-badge"
                        title={
                          formData.commission_rate_source === 'snapshot'
                            ? 'Rate locked at booking creation. Property edits after the booking date cannot change this commission.'
                            : formData.commission_rate_source === 'schedule'
                              ? 'Rate resolved from property commission schedule based on booking date.'
                              : 'Rate inherited from property base settings.'
                        }
                      >
                        🔒 {
                          formData.commission_rate_source === 'snapshot' ? 'Locked (snapshot)' :
                          formData.commission_rate_source === 'schedule' ? 'Locked (scheduled)' :
                          'Locked (base)'
                        }
                      </span>
                    )}
                    {isFixedCommission && (
                      <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200" data-testid="commission-mode-badge">
                        Fixed
                      </span>
                    )}
                  </Label>
                  {isFixedCommission ? (
                    <Input
                      data-testid="commission-fixed-input"
                      type="number"
                      step="0.01"
                      value={formData.commission_fixed_amount || ''}
                      onChange={(e) => handleInputChange('commission_fixed_amount', parseFloat(e.target.value) || 0)}
                      disabled={formData.commission_locked_by_snapshot && (user?.role === 'agent' || user?.role === 'customer')}
                    />
                  ) : (
                    <Input
                      data-testid="commission-pct-input"
                      type="number"
                      step="0.01"
                      value={formData.commission_percentage || ''}
                      onChange={(e) => handleInputChange('commission_percentage', parseFloat(e.target.value) || 0)}
                      disabled={formData.commission_locked_by_snapshot && (user?.role === 'agent' || user?.role === 'customer')}
                    />
                  )}
                  {formData.commission_locked_by_snapshot && (commissionConfig?._booking_snapshot?.effective_from || commissionConfig?._resolved_rate?.effective_from) && (
                    <p className="text-[10px] text-gray-500">
                      From schedule effective {commissionConfig?._booking_snapshot?.effective_from || commissionConfig?._resolved_rate?.effective_from}
                      {commissionConfig?._booking_snapshot?.schedule_note || commissionConfig?._resolved_rate?.note
                        ? ` — ${commissionConfig?._booking_snapshot?.schedule_note || commissionConfig?._resolved_rate?.note}`
                        : ''}
                    </p>
                  )}
                  {formData.commission_locked_by_snapshot && !commissionConfig?._booking_snapshot && commissionConfig?._resolved_rate?.source === 'base' && (
                    <p className="text-[10px] text-gray-500">
                      Inherited from property base rate (no schedule applies to booking date {formData.booking_date}).
                    </p>
                  )}
                </div>
                <div className="space-y-2"><Label>Referral Fees (%)</Label><Input data-testid="referral-fee-input" type="number" step="0.01" value={formData.referral_fee_percentage || ''} onChange={(e) => handleInputChange('referral_fee_percentage', parseFloat(e.target.value) || 0)} /></div>
              </div>

              {/* Commission Pool Breakdown */}
              {netCommission > 0 && (
                <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center gap-2 mb-1"><Calculator className="h-5 w-5 text-blue-600" /><span className="font-medium text-blue-800">Commission Pool Breakdown</span></div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                      <div><p className="text-gray-500 text-xs">Gross Commission</p><p className="font-mono font-semibold">{formatCurrency(grossCommission)}</p></div>
                      {referralFees > 0 && <div><p className="text-gray-500 text-xs">Referral Fees</p><p className="font-mono text-red-600">-{formatCurrency(referralFees)}</p></div>}
                      <div><p className="text-gray-500 text-xs">Net Commission</p><p className="font-mono font-bold text-blue-700" data-testid="net-commission-display">{formatCurrency(netCommission)}</p></div>
                    </div>
                    {isSamRumah && (
                      <div className="grid grid-cols-2 gap-3 text-sm pt-2 border-t border-blue-200">
                        <div><p className="text-gray-500 text-xs">SAM Rumah Pool (60%/2)</p><p className="font-mono font-semibold text-emerald-700">{formatCurrency(samRumahPool)}</p></div>
                        <div><p className="text-gray-500 text-xs">SP SAM Pool</p><p className="font-mono font-semibold text-blue-700">{formatCurrency(spSamPool)}</p></div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              <Separator />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2"><Label><Landmark className="h-4 w-4 inline mr-1 text-gray-400" />Bank Name</Label><Input value={formData.bank_name} onChange={(e) => handleInputChange('bank_name', e.target.value)} /></div>
                <div className="space-y-2"><Label>Loan Amount (RM)</Label><Input type="number" value={formData.loan_amount || ''} onChange={(e) => handleInputChange('loan_amount', parseFloat(e.target.value) || 0)} /></div>
                <div className="space-y-2">
                  <Label>Loan Type</Label>
                  <Select value={formData.loan_type} onValueChange={(v) => handleInputChange('loan_type', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="conventional">Conventional</SelectItem><SelectItem value="islamic">Islamic</SelectItem><SelectItem value="cash">Cash</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2"><Label><Calendar className="h-4 w-4 inline mr-1 text-gray-400" />Booking Date</Label><Input type="date" value={formData.booking_date} onChange={(e) => handleInputChange('booking_date', e.target.value)} /></div>
                <div className="space-y-2"><Label>SPA Signing Date *</Label><Input type="date" value={formData.signing_date} onChange={(e) => handleInputChange('signing_date', e.target.value)} /></div>
                <div className="space-y-2"><Label>Downpayment (RM)</Label><Input type="number" value={formData.downpayment || ''} onChange={(e) => handleInputChange('downpayment', parseFloat(e.target.value) || 0)} /></div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="full_loan" checked={formData.full_loan} onCheckedChange={(c) => handleInputChange('full_loan', c)} />
                <Label htmlFor="full_loan">Full Loan (100%)</Label>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ====== STEP 3: Commission Distribution ====== */}
      {currentStep === 3 && (
        <div className="space-y-4" data-testid="step-3-content">
          {/* Pool Header */}
          <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-blue-100 text-sm">Net Commission</p>
                  <p className="text-3xl font-bold" data-testid="commission-pool-display">{formatCurrency(netCommission)}</p>
                  <p className="text-blue-200 text-xs mt-1">{isFixedCommission ? `${formatCurrency(formData.commission_fixed_amount || 0)} fixed` : `${formatCurrency(formData.nett_price)} x ${formData.commission_percentage}%`}{referralFees > 0 ? ` - ${formatCurrency(referralFees)} referral` : ''}</p>
                </div>
                <Badge className="bg-white/20 text-white text-sm">{isSamRumah ? 'SAM Rumah' : 'SP SAM'}</Badge>
              </div>
              {isSamRumah && (
                <div className="grid grid-cols-2 gap-4 pt-3 border-t border-white/20">
                  <div><p className="text-blue-200 text-xs">SAM Rumah Pool (60%/2)</p><p className="text-xl font-bold">{formatCurrency(samRumahPool)}</p></div>
                  <div><p className="text-blue-200 text-xs">SP SAM Pool</p><p className="text-xl font-bold">{formatCurrency(spSamPool)}</p></div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ===== SAM RUMAH DISTRIBUTION (only for SAM Rumah entity) ===== */}
          {isSamRumah && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center font-bold text-sm">SR</div>
                  SAM Rumah Distribution
                </CardTitle>
                <CardDescription>Customer hierarchy: Upline 1-4, Introducer, Customer | Formula: Gross &rarr; -8% SST &rarr; -5% Platform Fee &rarr; Net</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="hidden md:grid grid-cols-12 gap-2 mb-2 px-3 text-xs font-semibold text-gray-500 uppercase">
                  <div className="col-span-3">Recipient</div>
                  <div className="col-span-2 text-right">Rate%</div>
                  <div className="col-span-2 text-right">Gross (RM)</div>
                  <div className="col-span-2 text-right">SST 8%</div>
                  <div className="col-span-1 text-right">Plat 5%</div>
                  <div className="col-span-2 text-right">Net (RM)</div>
                </div>
                <div className="space-y-2">
                  {samRumahChainCalc.map((member, idx) => {
                    const colors = getRankColor(member.level || member.rank || 'upline');
                    return (
                      <div key={idx} className={`grid grid-cols-12 gap-2 items-center p-2.5 rounded-lg border ${colors.bg} ${colors.border}`} data-testid={`sr-recipient-row-${idx}`}>
                        <div className="col-span-3 flex items-center gap-1.5 min-w-0">
                          {member.is_manual ? (
                            <>
                              <select
                                className="h-7 text-xs rounded border-gray-300 bg-white px-1 font-semibold shrink-0"
                                value={member.level || 'customer'}
                                onChange={(e) => updateSrRecipientRole(idx, e.target.value)}
                                data-testid={`sr-role-select-${idx}`}
                              >
                                {SR_ROLE_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                              <Input
                                className="h-7 text-xs flex-1 min-w-0"
                                placeholder="Name"
                                value={member.name || ''}
                                onChange={(e) => updateSrRecipientName(idx, e.target.value)}
                                data-testid={`sr-name-input-${idx}`}
                              />
                            </>
                          ) : (
                            <>
                              <Badge className={`${colors.badge} text-white text-xs shrink-0`}>{member.level_display || member.rank_display || member.level?.toUpperCase()}</Badge>
                              <span className="text-xs font-medium truncate">{member.name}</span>
                            </>
                          )}
                          <button onClick={() => removeSrRecipient(idx)} className="shrink-0 text-gray-400 hover:text-red-500 transition-colors" data-testid={`sr-remove-btn-${idx}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                          {member.user_id && member.level === 'customer' && samRumahChain.length === 1 && (
                            <Button
                              size="sm"
                              className="h-6 text-xs px-2 bg-emerald-600 hover:bg-emerald-700 text-white gap-1 shrink-0"
                              onClick={() => loadChainFromUser({ id: member.user_id, full_name: member.name })}
                              data-testid={`sr-load-chain-row-${idx}`}
                            >
                              <Users className="h-3 w-3" /> Load Chain
                            </Button>
                          )}
                        </div>
                        <div className="col-span-2">
                          <Input type="number" step="0.1" min="0" max="100" placeholder="0.0" className="w-full text-right h-8 text-sm bg-white border-gray-300 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500" value={member.rate_percentage || ''} onChange={(e) => updateSamRumahRate(idx, 'rate_percentage', e.target.value)} data-testid={`sr-rate-input-${idx}`} />
                        </div>
                        <div className="col-span-2 text-right font-mono text-xs font-semibold">{formatCurrency(member.gross_comm || 0)}</div>
                        <div className="col-span-2 text-right font-mono text-xs text-red-600">-{formatCurrency(member.sst_amount || 0)}</div>
                        <div className="col-span-1 text-right font-mono text-xs text-amber-600">-{formatCurrency(member.platform_fee || 0)}</div>
                        <div className="col-span-2 text-right font-mono text-sm font-bold text-emerald-700">{formatCurrency(member.nett_comm || 0)}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Search & Manual Add Section */}
                <div className="mt-3 border border-dashed border-emerald-300 rounded-lg p-3 space-y-3" data-testid="sr-manual-entry">
                  {/* User Search */}
                  <div className="relative">
                    <div className="flex items-center gap-2">
                      <Search className="h-4 w-4 text-gray-400 shrink-0" />
                      <Input
                        placeholder="Search by User ID (e.g., NEG0001, C-XXXXXX), name, IC, phone, or email..."
                        value={srSearchQuery}
                        onChange={(e) => handleSrSearch(e.target.value)}
                        className="h-8 text-sm"
                        data-testid="sr-user-search"
                      />
                    </div>
                    {/* Search Results Dropdown */}
                    {(srSearchResults.length > 0 || srSearching) && srSearchQuery.length >= 2 && (
                      <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="sr-search-results">
                        {srSearching && <div className="p-3 text-center text-sm text-gray-500">Searching...</div>}
                        {!srSearching && srSearchResults.length === 0 && (
                          <div className="p-3 text-center text-sm text-gray-400">No users found</div>
                        )}
                        {srSearchResults.map(u => (
                          <div key={u.id} className="p-2 hover:bg-emerald-50 border-b last:border-0 flex items-center justify-between group" data-testid={`sr-search-result-${u.id}`}>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium truncate">{u.full_name}</p>
                                {u.user_code && (
                                  <span className="shrink-0 font-mono text-[10px] text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                                    {u.user_code}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 truncate">{u.email} {u.phone ? `| ${u.phone}` : ''} {u.ic_number ? `| IC: ${u.ic_number}` : ''}</p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0 ml-2">
                              <Button
                                size="sm"
                                className="h-6 text-xs px-2.5 bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                                onClick={() => loadChainFromUser(u)}
                                data-testid={`sr-load-chain-${u.id}`}
                              >
                                <Users className="h-3 w-3" /> Load Chain
                              </Button>
                              {SR_ROLE_OPTIONS.slice(0, 3).map(role => (
                                <Button
                                  key={role.value}
                                  size="sm"
                                  variant="outline"
                                  className="h-6 text-xs px-2 opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={() => addSrRecipientFromSearch(u, role.value)}
                                  data-testid={`sr-add-as-${role.value}-${u.id}`}
                                >
                                  + {role.label}
                                </Button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Quick Add Buttons */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-gray-500 font-medium">Add manually:</span>
                    {SR_ROLE_OPTIONS.map(role => (
                      <Button
                        key={role.value}
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                        onClick={() => addSrManualRecipient(role.value)}
                        data-testid={`sr-add-manual-${role.value}`}
                      >
                        <Plus className="h-3 w-3" /> {role.label}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="mt-3 p-3 bg-emerald-50 rounded-lg">
                  <div className="grid grid-cols-12 gap-2 text-sm">
                    <div className="col-span-5 font-medium text-gray-600">SAM Rumah Total</div>
                    <div className="col-span-2 text-right font-mono font-semibold">{formatCurrency(srTotalGross)}</div>
                    <div className="col-span-2 text-right font-mono text-red-600">-{formatCurrency(srTotalSST)}</div>
                    <div className="col-span-1 text-right font-mono text-amber-600">-{formatCurrency(srTotalPlatform)}</div>
                    <div className="col-span-2 text-right font-mono font-bold text-emerald-700">{formatCurrency(srTotalNett)}</div>
                  </div>
                </div>

                {/* Company retained profit — SAM Rumah Sdn Bhd */}
                <div
                  className="mt-3 p-3 rounded-lg border-2 border-dashed border-emerald-300 bg-gradient-to-br from-emerald-50/60 to-white"
                  data-testid="sam-rumah-retained-block"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-md bg-emerald-600 text-white flex items-center justify-center text-[10px] font-bold">
                      $
                    </div>
                    <div className="font-semibold text-emerald-900 text-sm">
                      SAM Rumah Sdn Bhd — Retained Profit
                    </div>
                  </div>
                  <div className="space-y-1 text-xs text-gray-700 pl-8">
                    <div className="flex justify-between">
                      <span>
                        Unallocated pool remainder{' '}
                        <span className="text-gray-400">
                          ({formatCurrency(samRumahPool)} − {formatCurrency(srTotalGross)})
                        </span>
                      </span>
                      <span className="font-mono">{formatCurrency(samRumahUnallocated)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>+ Platform fees collected</span>
                      <span className="font-mono">{formatCurrency(srTotalPlatform)}</span>
                    </div>
                    <div className="flex justify-between pt-1.5 mt-1.5 border-t border-emerald-200 font-semibold text-emerald-800">
                      <span>= Total retained by SAM Rumah</span>
                      <span
                        className="font-mono text-base"
                        data-testid="sam-rumah-retained-amount"
                      >
                        {formatCurrency(samRumahRetained)}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ===== SP SAM DISTRIBUTION ===== */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-500 text-white flex items-center justify-center font-bold text-sm">SP</div>
                SP SAM Distribution
              </CardTitle>
              <CardDescription>Agent hierarchy: NEG/RM/AVP/SVP/EVP (-SST -Platform) + PIC/Trip (-SST only) + SP SAM Profit (no deductions)</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Agent Hierarchy (NEG, RM, AVP, SVP, EVP) - with platform fee */}
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Users className="h-4 w-4" /> Agent Hierarchy</h4>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-blue-600 hover:text-blue-800"
                  onClick={() => {
                    const b = bookings.find(x => x.id === formData.booking_id);
                    const chainAgentId = b?.agent_id || b?.agent?.id || existingClaim?.agent_id || user?.id;
                    if (chainAgentId) loadAgentChain(chainAgentId);
                  }}
                  data-testid="reload-sp-chain-btn"
                >
                  Reload Hierarchy
                </Button>
              </div>
              <div className="hidden md:grid grid-cols-12 gap-2 mb-2 px-3 text-xs font-semibold text-gray-500 uppercase">
                <div className="col-span-4">Recipient (Rank · Agent)</div>
                <div className="col-span-1 text-right">Rate%</div>
                <div className="col-span-2 text-right">Gross (RM)</div>
                <div className="col-span-1 text-right">SST 8%</div>
                <div className="col-span-1 text-right">Plat 5%</div>
                <div className="col-span-3 text-right">Net (RM)</div>
              </div>
              <div className="space-y-2">
                {spSamChainCalc.map((member, idx) => {
                  const colors = getRankColor(member.rank);
                  const isSearchOpen = spSlotSearchOpen === idx;
                  return (
                    <div key={idx} className={`grid grid-cols-12 gap-2 items-center p-2.5 rounded-lg border ${colors.bg} ${colors.border} relative`}>
                      {/* Rank + Agent picker */}
                      <div className="col-span-4 flex items-center gap-2 min-w-0">
                        <Badge className={`${colors.badge} text-white text-xs shrink-0`} data-testid={`sp-slot-rank-${member.rank}`}>
                          {member.rank_display || member.rank?.toUpperCase()}
                        </Badge>
                        {member.user_id ? (
                          <div className="flex items-center gap-1 min-w-0 flex-1">
                            <div className="min-w-0">
                              <div className="text-xs font-medium truncate flex items-center gap-1.5">
                                {member.name}
                                {member.user_code && (
                                  <span className="font-mono text-[10px] text-indigo-600 bg-white px-1 py-0.5 rounded">
                                    {member.user_code}
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => openSpSlotSearch(idx)}
                              className="ml-auto text-[10px] text-blue-600 hover:text-blue-800 shrink-0"
                              data-testid={`sp-slot-change-${member.rank}`}
                            >
                              Change
                            </button>
                            <button
                              type="button"
                              onClick={() => clearSpSlotAgent(idx)}
                              className="text-[10px] text-red-500 hover:text-red-700 shrink-0"
                              data-testid={`sp-slot-clear-${member.rank}`}
                            >
                              Clear
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openSpSlotSearch(idx)}
                            className="text-xs text-blue-600 hover:text-blue-800 underline underline-offset-2"
                            data-testid={`sp-slot-assign-${member.rank}`}
                          >
                            + Assign agent
                          </button>
                        )}
                      </div>

                      {/* Rate */}
                      <div className="col-span-1">
                        <Input
                          type="number" step="0.1" min="0" max="100" placeholder="0.0"
                          className="w-full text-right h-8 text-sm bg-white border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          value={member.rate_percentage || ''}
                          onChange={(e) => updateSpSamRate(idx, 'rate_percentage', e.target.value)}
                          data-testid={`sp-rate-input-${idx}`}
                        />
                      </div>
                      <div className="col-span-2 text-right font-mono text-xs font-semibold">{formatCurrency(member.gross_comm || 0)}</div>
                      <div className="col-span-1 text-right font-mono text-xs text-red-600">-{formatCurrency(member.sst_amount || 0)}</div>
                      <div className="col-span-1 text-right font-mono text-xs text-amber-600">-{formatCurrency(member.platform_fee || 0)}</div>
                      <div className="col-span-3 text-right font-mono text-sm font-bold text-blue-700">{formatCurrency(member.nett_comm || 0)}</div>

                      {/* Inline agent search popover */}
                      {isSearchOpen && (
                        <div
                          className="col-span-12 mt-2 p-2 bg-white rounded-md border border-blue-300 shadow-sm relative"
                          data-testid={`sp-slot-search-${member.rank}`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Input
                              autoFocus
                              value={spSlotSearchQuery}
                              onChange={(e) => handleSpSlotSearch(e.target.value)}
                              placeholder="Search by name, user ID (NEG0001), email, phone..."
                              className="h-8 text-sm"
                              data-testid={`sp-slot-search-input-${member.rank}`}
                            />
                            <button
                              type="button"
                              onClick={() => setSpSlotSearchOpen(null)}
                              className="text-xs text-gray-500 hover:text-gray-700 shrink-0"
                            >
                              Close
                            </button>
                          </div>
                          {spSlotSearchResults.length > 0 && (
                            <div className="max-h-44 overflow-y-auto rounded border border-gray-200">
                              {spSlotSearchResults.map(u => (
                                <button
                                  type="button"
                                  key={u.id}
                                  onClick={() => assignSpSlotAgent(idx, u)}
                                  className="w-full flex items-center gap-2 text-left px-2 py-1.5 text-xs hover:bg-blue-50 border-b last:border-b-0"
                                  data-testid={`sp-slot-result-${member.rank}-${u.id}`}
                                >
                                  <span className="font-medium truncate">{u.full_name}</span>
                                  {u.user_code && (
                                    <span className="font-mono text-[10px] text-indigo-600 bg-indigo-50 px-1 py-0.5 rounded">
                                      {u.user_code}
                                    </span>
                                  )}
                                  <span className="text-[10px] text-gray-500 truncate ml-auto">{u.email}</span>
                                </button>
                              ))}
                            </div>
                          )}
                          {spSlotSearchQuery.length >= 2 && spSlotSearchResults.length === 0 && (
                            <p className="text-[11px] text-gray-500 text-center py-2">No matching users.</p>
                          )}
                          {spSlotSearchQuery.length < 2 && (
                            <p className="text-[11px] text-gray-400 text-center py-2">
                              Type at least 2 characters. Tip: paste an agent user code (e.g. <code>NEG0001</code>) for an exact match.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                <div className="grid grid-cols-12 gap-2 text-sm">
                  <div className="col-span-5 font-medium text-gray-600">Agent Hierarchy Total</div>
                  <div className="col-span-2 text-right font-mono font-semibold">{formatCurrency(spAgentTotalGross)}</div>
                  <div className="col-span-2 text-right font-mono text-red-600">-{formatCurrency(spAgentTotalSST)}</div>
                  <div className="col-span-1 text-right font-mono text-amber-600">-{formatCurrency(spAgentTotalPlatform)}</div>
                  <div className="col-span-2 text-right font-mono font-bold text-blue-700">{formatCurrency(spAgentTotalNett)}</div>
                </div>
              </div>

              <Separator className="my-4" />

              {/* PIC + Trip + SP SAM Profit */}
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Briefcase className="h-4 w-4" /> PIC, Trip & SP SAM Profit</h4>
              {/* V4.20 — Step 3 Task 2: Multi-PIC display. When the property has
                  more than one PIC assignment, render all of them (with tier +
                  split) as a read-only info card so the closer/PIC/Admin can
                  verify the property's PIC config at a glance. Single-PIC
                  projects continue to use the compact dropdown below. */}
              {picAssignments.length > 1 && (
                <div className="mb-3 p-3 rounded-lg border border-sky-300 bg-sky-50/60" data-testid="pic-assignments-multi">
                  <div className="text-xs font-semibold text-sky-900 mb-2 flex items-center gap-2">
                    <Badge className="bg-sky-600 text-white text-[10px]">MULTI-PIC</Badge>
                    Property has {picAssignments.length} PIC{picAssignments.length > 1 ? 's' : ''} — using configured split
                  </div>
                  <div className="space-y-1">
                    {picAssignments.map((a, i) => (
                      <div key={a.user_id || i} className="grid grid-cols-12 gap-2 items-center text-xs" data-testid={`pic-assignment-row-${i}`}>
                        <div className="col-span-4 truncate font-medium">
                          {a.full_name || a.email || a.user_id || '—'}
                        </div>
                        <div className="col-span-3">
                          <Badge variant="outline" className="text-[10px] uppercase">{a.tier || 'primary'}</Badge>
                        </div>
                        <div className="col-span-5 text-right font-mono">
                          {a.split_mode === 'fixed'
                            ? `RM ${Number(a.split_value || 0).toLocaleString()}`
                            : `${Number(a.split_value || 0).toFixed(2)}%`}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-3">
                {/* PIC */}
                <div className="grid grid-cols-12 gap-2 items-center p-3 rounded-lg border border-sky-200 bg-sky-50">
                  <div className="col-span-3 flex items-center gap-2">
                    <Badge className="bg-sky-600 text-white text-xs">PIC</Badge>
                    <Select value={formData.pic_user_id} onValueChange={(v) => { const p = picUsers.find(u => u.id === v); handleInputChange('pic_user_id', v); handleInputChange('pic_user_name', p?.full_name || ''); }}>
                      <SelectTrigger className="h-8 text-xs" data-testid="pic-select"><SelectValue placeholder="Select PIC" /></SelectTrigger>
                      <SelectContent>{picUsers.map(p => <SelectItem key={p.id} value={p.id}>{p.full_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Input type="number" step="0.1" min="0" max="100" placeholder="7.0" className="w-full text-right h-8 text-sm bg-white" value={formData.pic_rate || ''} onChange={(e) => handleInputChange('pic_rate', parseFloat(e.target.value) || 0)} data-testid="pic-rate-input" /></div>
                  <div className="col-span-2 text-right font-mono text-xs font-semibold">{formatCurrency(picCalc.gross_comm)}</div>
                  <div className="col-span-2 text-right font-mono text-xs text-red-600">-{formatCurrency(picCalc.sst_amount)}</div>
                  <div className="col-span-1 text-right font-mono text-xs text-gray-400">&mdash;</div>
                  <div className="col-span-2 text-right font-mono text-sm font-bold text-sky-700">{formatCurrency(picCalc.nett_comm)}</div>
                </div>
                {/* Trip */}
                <div className="grid grid-cols-12 gap-2 items-center p-3 rounded-lg border border-violet-200 bg-violet-50">
                  <div className="col-span-3 flex items-center gap-2"><Badge className="bg-violet-600 text-white text-xs">TRIP</Badge><span className="text-xs font-medium">Trip Wallet</span></div>
                  <div className="col-span-2"><Input type="number" step="0.1" min="0" max="100" placeholder="3.0" className="w-full text-right h-8 text-sm bg-white" value={formData.trip_rate || ''} onChange={(e) => handleInputChange('trip_rate', parseFloat(e.target.value) || 0)} data-testid="trip-rate-input" /></div>
                  <div className="col-span-2 text-right font-mono text-xs font-semibold">{formatCurrency(tripCalc.gross_comm)}</div>
                  <div className="col-span-2 text-right font-mono text-xs text-red-600">-{formatCurrency(tripCalc.sst_amount)}</div>
                  <div className="col-span-1 text-right font-mono text-xs text-gray-400">&mdash;</div>
                  <div className="col-span-2 text-right font-mono text-sm font-bold text-violet-700">{formatCurrency(tripCalc.nett_comm)}</div>
                </div>
                {/* SP SAM Profit */}
                <div className="grid grid-cols-12 gap-2 items-center p-3 rounded-lg border border-slate-300 bg-slate-50">
                  <div className="col-span-3 flex items-center gap-2"><Badge className="bg-slate-700 text-white text-xs">SP SAM</Badge><span className="text-xs font-medium">Profit/Income</span></div>
                  <div className="col-span-2"><Input type="number" step="0.1" min="0" max="100" placeholder="10.0" className="w-full text-right h-8 text-sm bg-white" value={formData.sp_sam_profit_rate || ''} onChange={(e) => handleInputChange('sp_sam_profit_rate', parseFloat(e.target.value) || 0)} data-testid="sp-sam-profit-input" /></div>
                  <div className="col-span-2 text-right font-mono text-xs font-semibold">{formatCurrency(profitCalc.gross_comm)}</div>
                  <div className="col-span-2 text-right font-mono text-xs text-green-600">NO SST</div>
                  <div className="col-span-1 text-right font-mono text-xs text-gray-400">&mdash;</div>
                  <div className="col-span-2 text-right font-mono text-sm font-bold text-slate-800">{formatCurrency(profitCalc.nett_comm)}</div>
                </div>
              </div>

              {/* SP SAM Total */}
              <div className="mt-3 p-3 bg-gray-100 rounded-lg">
                <div className="flex justify-between text-sm font-bold">
                  <span>SP SAM Distribution Total</span>
                  <span className="font-mono text-blue-700">{formatCurrency(spTotalNett)}</span>
                </div>
                <div className="text-xs text-gray-500 mt-1 flex justify-between">
                  <span>Platform Fees to SP SAM Revenue: <span className="font-semibold text-amber-700">{formatCurrency(spAgentTotalPlatform)}</span></span>
                  <span>SP SAM Total Revenue: <span className="font-semibold text-slate-800">{formatCurrency(spSamRevenue)}</span></span>
                </div>
              </div>

              {/* Company retained profit — SP SAM */}
              <div
                className="mt-3 p-3 rounded-lg border-2 border-dashed border-blue-300 bg-gradient-to-br from-blue-50/60 to-white"
                data-testid="sp-sam-retained-block"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-md bg-blue-600 text-white flex items-center justify-center text-[10px] font-bold">
                    $
                  </div>
                  <div className="font-semibold text-blue-900 text-sm">
                    SP SAM — Retained Profit (safety net)
                  </div>
                </div>
                <div className="space-y-1 text-xs text-gray-700 pl-8">
                  <div className="flex justify-between">
                    <span>
                      Unallocated SP pool{' '}
                      <span className="text-gray-400">
                        (after agents, PIC, Trip, profit_rate)
                      </span>
                    </span>
                    <span className="font-mono">{formatCurrency(spSamUnallocated)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>+ Platform fees from agents</span>
                    <span className="font-mono">{formatCurrency(spAgentTotalPlatform)}</span>
                  </div>
                  <div className="flex justify-between pt-1.5 mt-1.5 border-t border-blue-200 font-semibold text-blue-800">
                    <span>= Total retained by SP SAM</span>
                    <span
                      className="font-mono text-base"
                      data-testid="sp-sam-retained-amount"
                    >
                      {formatCurrency(spSamRetained)}
                    </span>
                  </div>
                  {spSamUnallocated < 0.01 && (
                    <div className="text-[10px] text-emerald-700 italic pt-1">
                      Pool fully absorbed by profit_rate — residue is RM 0.00 (good).
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ===== FAST COMM ===== */}
          <Card className={`border-2 ${isFastComm ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200'}`}>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2"><Zap className={`h-5 w-5 ${isFastComm ? 'text-yellow-600' : 'text-gray-400'}`} /> Fast Comm (Agent Advance)</CardTitle>
              <CardDescription>
                Agent can opt for an upfront commission payout. Available options are configured per project.
                {commissionConfig?.property_name && (
                  <span className="block text-xs text-gray-500 mt-1">
                    Project: <span className="font-semibold">{commissionConfig.property_name}</span> •
                    Enabled: <span className="font-mono">{(commissionConfig.fast_comm_enabled || []).map(o => `${o}%`).join(', ') || 'none'}</span>
                  </span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Label className="text-sm font-medium">Fast Comm Option:</Label>
                <Select value={formData.fast_comm} onValueChange={(v) => handleInputChange('fast_comm', v)}>
                  <SelectTrigger className="w-72" data-testid="fast-comm-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(commissionConfig?.fast_comm_enabled || ['0', '50', '100']).includes('0') && (
                      <SelectItem value="0" data-testid="fast-comm-option-0">0% — Wait for developer payout</SelectItem>
                    )}
                    {(commissionConfig?.fast_comm_enabled || ['0', '50', '100']).includes('50') && (
                      <SelectItem value="50" data-testid="fast-comm-option-50">50% — Upfront, no charges</SelectItem>
                    )}
                    {(commissionConfig?.fast_comm_enabled || ['0', '50', '100']).includes('100') && (
                      <SelectItem value="100" data-testid="fast-comm-option-100">100% — Upfront, 16.67% fee + 8% SST</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* V4.6 — Payout Method (only shown when fast_comm in {50,100}) */}
              {isFastComm && (
                <div className="border border-blue-200 bg-blue-50/40 rounded-lg p-4" data-testid="fast-comm-payout-method-panel">
                  <Label className="text-sm font-medium text-gray-800">Payout Destination</Label>
                  <p className="text-xs text-gray-500 mb-3">Where should the upfront commission be sent?</p>
                  <RadioGroup
                    value={formData.fast_comm_payout_method || 'wallet'}
                    onValueChange={(v) => handleInputChange('fast_comm_payout_method', v)}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                  >
                    <Label
                      htmlFor="payout-wallet"
                      className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition ${
                        (formData.fast_comm_payout_method || 'wallet') === 'wallet'
                          ? 'border-blue-500 bg-white shadow-sm'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                      data-testid="payout-method-wallet"
                    >
                      <RadioGroupItem value="wallet" id="payout-wallet" className="mt-0.5" />
                      <div className="flex-1">
                        <p className="font-semibold text-sm text-gray-800">Pay to In-App Wallet</p>
                        <p className="text-xs text-gray-500 mt-1">Instant credit on approval. Agent withdraws to bank later via the Wallet section.</p>
                      </div>
                    </Label>
                    <Label
                      htmlFor="payout-direct-bank"
                      className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition ${
                        formData.fast_comm_payout_method === 'direct_bank'
                          ? 'border-emerald-500 bg-white shadow-sm'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                      data-testid="payout-method-direct-bank"
                    >
                      <RadioGroupItem value="direct_bank" id="payout-direct-bank" className="mt-0.5" />
                      <div className="flex-1">
                        <p className="font-semibold text-sm text-gray-800">Pay Direct to Bank Account</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Account team transfers manually to the agent's saved bank account, then marks paid here. Status visible to the agent.
                        </p>
                      </div>
                    </Label>
                  </RadioGroup>
                  {formData.fast_comm_payout_method === 'direct_bank' && (
                    <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded mt-2 px-2 py-1.5" data-testid="direct-bank-hint">
                      <strong>Note:</strong> Make sure the agent has a verified bank account in their profile before submitting. Wallet will not be credited for this claim.
                    </p>
                  )}
                </div>
              )}

              {isFastComm && agentNetAfterPlatform > 0 && (
                <div className="p-4 bg-white rounded-lg border border-yellow-300 space-y-2">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div><p className="text-gray-500 text-xs">Agent Net (after SST+Platform)</p><p className="font-mono font-semibold">{formatCurrency(agentNetAfterPlatform)}</p></div>
                    {isFastComm100 ? (
                      <>
                        <div><p className="text-gray-500 text-xs">Fast Comm Fee (16.67%)</p><p className="font-mono text-red-600">-{formatCurrency(fastCommFee)}</p></div>
                        <div><p className="text-gray-500 text-xs">SST on Fee (8%)</p><p className="font-mono text-red-600">-{formatCurrency(fastCommSST)}</p></div>
                      </>
                    ) : (
                      <>
                        <div><p className="text-gray-500 text-xs">Advance Portion (50%)</p><p className="font-mono font-semibold text-blue-700">{formatCurrency(agentNetAfterPlatform * 0.5)}</p></div>
                        <div><p className="text-gray-500 text-xs">Charges</p><p className="font-mono text-green-700">RM 0.00 — no fees</p></div>
                      </>
                    )}
                    <div><p className="text-gray-500 text-xs">Net to Agent Wallet</p><p className="font-mono font-bold text-green-700 text-lg">{formatCurrency(agentWalletAmount)}</p></div>
                  </div>
                  <div className="text-[11px] text-gray-600 pt-2 border-t border-yellow-200">
                    Advance computed from{' '}
                    <span className="font-semibold">
                      {soldAgentSlots.length > 0
                        ? `${soldAgentSlots.length} slot(s) assigned to the closing agent`
                        : 'the NEG slot (default — no slot is explicitly assigned to the closing agent yet)'}
                    </span>.
                  </div>
                  <div className="text-xs text-gray-500">
                    {isFastComm100
                      ? <>Flow: SP SAM deducts fees &rarr; transfers <span className="font-semibold">{formatCurrency(agentWalletAmount)}</span> to SAM Rumah &rarr; SAM Rumah deposits to Agent wallet</>
                      : <>Flow: SAM Rumah advances <span className="font-semibold">{formatCurrency(agentWalletAmount)}</span> to Agent wallet now &rarr; remaining 50% paid out when developer settles commission</>
                    }
                  </div>
                </div>
              )}

              {isFastComm && agentNetAfterPlatform === 0 && (
                <div className="p-3 bg-white rounded-lg border border-dashed border-yellow-300 text-xs text-gray-600">
                  Fast Comm advance is <span className="font-mono">RM 0.00</span> because no rate is set on the closing agent's slot(s) yet.
                  Assign an agent to at least one rank slot above (NEG, RM, AVP, SVP, EVP) and set the rate — the advance will recompute automatically.
                </div>
              )}
            </CardContent>
          </Card>

          {/* ===== GRAND TOTAL ===== */}
          <Card className="border-2 border-gray-300">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Calculator className="h-5 w-5" /> Commission Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-5 gap-2 text-xs font-semibold text-gray-500 uppercase px-2">
                <div>Section</div>
                <div className="text-right">Gross</div>
                <div className="text-right">SST (8%)</div>
                <div className="text-right">Platform (5%)</div>
                <div className="text-right">Net Payable</div>
              </div>
              <Separator />
              {isSamRumah && (
                <div className="grid grid-cols-5 gap-2 text-sm px-2">
                  <div className="text-gray-600">SAM Rumah Pool</div>
                  <div className="text-right font-mono">{formatCurrency(srTotalGross)}</div>
                  <div className="text-right font-mono text-red-600">-{formatCurrency(srTotalSST)}</div>
                  <div className="text-right font-mono text-amber-600">-{formatCurrency(srTotalPlatform)}</div>
                  <div className="text-right font-mono font-semibold text-emerald-700">{formatCurrency(srTotalNett)}</div>
                </div>
              )}
              <div className="grid grid-cols-5 gap-2 text-sm px-2">
                <div className="text-gray-600">SP SAM Pool</div>
                <div className="text-right font-mono">{formatCurrency(spTotalGross)}</div>
                <div className="text-right font-mono text-red-600">-{formatCurrency(spTotalSST)}</div>
                <div className="text-right font-mono text-amber-600">-{formatCurrency(spTotalPlatform)}</div>
                <div className="text-right font-mono font-semibold text-blue-700">{formatCurrency(spTotalNett)}</div>
              </div>
              <Separator />
              <div className="grid grid-cols-5 gap-2 text-base font-bold px-2 py-1 bg-gray-50 rounded">
                <div>GRAND TOTAL</div>
                <div className="text-right font-mono">{formatCurrency(grandTotalGross)}</div>
                <div className="text-right font-mono text-red-600">-{formatCurrency(grandTotalSST)}</div>
                <div className="text-right font-mono text-amber-600">-{formatCurrency(grandTotalPlatform)}</div>
                <div className="text-right font-mono text-blue-700" data-testid="grand-total-nett">{formatCurrency((isSamRumah ? srTotalNett : 0) + spTotalNett)}</div>
              </div>
              {isFastComm && (
                <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2"><Zap className="h-4 w-4 text-yellow-600" /> Fast Comm: Agent receives <span className="font-bold text-green-700">{formatCurrency(agentWalletAmount)}</span> upfront</span>
                  <span className="text-xs text-gray-500">Fee: {formatCurrency(fastCommTotalDeduction)} to SP SAM</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ====== STEP 4: Documents ====== */}
      {currentStep === 4 && (
        <div className="space-y-4" data-testid="step-4-content">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Upload className="h-5 w-5" /> Supporting Documents</CardTitle>
              <CardDescription>{!claimId && <span className="text-orange-600">Save as draft first to enable uploads</span>}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <Label className="text-sm font-medium mb-3 block">Document Status Checklist</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {['bank_lo', 'la', 'spa', 'sales_form', 'booking_fee', 'dp_slip'].map(item => {
                    // Defensive: legacy claims may persist status_checklist as `{}` instead of `[]`.
                    // Coerce here so a single bad row never crashes the whole Step 4 render.
                    const sc = Array.isArray(formData.status_checklist) ? formData.status_checklist : [];
                    return (
                      <div key={item} className="flex items-center gap-2">
                        <Checkbox id={`check_${item}`} checked={sc.includes(item)} onCheckedChange={() => {
                          setFormData(prev => {
                            const safe = Array.isArray(prev.status_checklist) ? prev.status_checklist : [];
                            return {
                              ...prev,
                              status_checklist: safe.includes(item) ? safe.filter(i => i !== item) : [...safe, item],
                            };
                          });
                        }} />
                        <Label htmlFor={`check_${item}`} className="text-sm capitalize">{item.replace(/_/g, ' ')}</Label>
                      </div>
                    );
                  })}
                </div>
              </div>
              <Separator />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {DOCUMENT_TYPES.map((docType) => {
                  const Icon = docType.icon;
                  // Defensive: legacy claims may persist documents[key] as a single
                  // object instead of an array. Always coerce to array before rendering.
                  const rawDocs = uploadedDocs[docType.key];
                  const docs = Array.isArray(rawDocs) ? rawDocs : (rawDocs ? [rawDocs] : []);
                  const isUploading = uploadProgress[docType.key] !== undefined;
                  return (
                    <div key={docType.key} className={`p-4 border rounded-lg ${docType.required && docs.length === 0 ? 'border-orange-200 bg-orange-50' : 'border-gray-200 bg-white'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Icon className="h-5 w-5 text-gray-500" />
                          <span className="font-medium text-sm">{docType.label}</span>
                          {docType.required && <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">Required</Badge>}
                        </div>
                        {docs.length > 0 && <Badge className="bg-green-100 text-green-700 text-xs">{docs.length} file(s)</Badge>}
                      </div>
                      {docs.map((doc, idx) => (
                        <div key={idx} className="flex items-center justify-between p-1.5 bg-gray-50 rounded text-xs mb-1">
                          <div className="flex items-center gap-1.5"><FileText className="h-3 w-3 text-gray-400" /><span className="truncate max-w-[150px]">{doc.original_name}</span></div>
                          <CheckCircle className="h-3 w-3 text-green-500" />
                        </div>
                      ))}
                      {isUploading && <Progress value={uploadProgress[docType.key]} className="h-1.5 mb-2" />}
                      <input ref={(el) => (fileInputRefs.current[docType.key] = el)} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileSelect(docType.key)} />
                      <Button variant="outline" size="sm" className="w-full mt-2" onClick={() => fileInputRefs.current[docType.key]?.click()} disabled={!claimId || isUploading}>
                        <Upload className="h-4 w-4 mr-1" /> {docs.length > 0 ? 'Add More' : 'Upload'}
                      </Button>
                    </div>
                  );
                })}
              </div>
              <div className="space-y-2"><Label>Additional Remarks</Label><Textarea value={formData.remark} onChange={(e) => handleInputChange('remark', e.target.value)} rows={3} /></div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ====== STEP 5: Declaration ====== */}
      {currentStep === 5 && (
        <div className="space-y-4" data-testid="step-5-content">
          <Card>
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><FileText className="h-5 w-5" /> Claim Summary</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><p className="text-gray-500">Entity</p><Badge className={isSamRumah ? 'bg-emerald-500' : 'bg-blue-500'}>{isSamRumah ? 'SAM Rumah' : 'SP SAM'}</Badge></div>
                <div><p className="text-gray-500">Project</p><p className="font-semibold">{formData.project_name || '-'}</p></div>
                <div><p className="text-gray-500">Unit</p><p className="font-semibold">{formData.unit_number || '-'}</p></div>
                <div><p className="text-gray-500">Buyer</p><p className="font-semibold">{formData.customer_full_name || '-'}</p></div>
                <div><p className="text-gray-500">Net Price</p><p className="font-mono font-semibold">{formatCurrency(formData.nett_price)}</p></div>
                <div><p className="text-gray-500">{isFixedCommission ? 'Commission (Fixed)' : 'Commission'}</p><p className="font-semibold">{formatCommissionRate(formData)}</p></div>
                <div><p className="text-gray-500">Net Commission</p><p className="font-mono font-bold text-blue-700">{formatCurrency(netCommission)}</p></div>
                <div><p className="text-gray-500">Fast Comm</p><Badge className={isFastComm ? 'bg-yellow-500' : 'bg-gray-300'}>{isFastComm ? '100% Active' : 'Off'}</Badge></div>
              </div>

              <Separator />

              {/* Summary Table */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pool</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead className="text-right">Rate%</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">SST</TableHead>
                    <TableHead className="text-right">Platform</TableHead>
                    <TableHead className="text-right">Net</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isSamRumah && samRumahChainCalc.filter(m => m.rate_percentage > 0).map((m, idx) => (
                    <TableRow key={`sr-${idx}`}>
                      {idx === 0 && <TableCell rowSpan={samRumahChainCalc.filter(x => x.rate_percentage > 0).length} className="font-medium text-emerald-700 align-top">SAM Rumah</TableCell>}
                      <TableCell><Badge variant="outline" className="text-xs">{m.level_display || m.rank_display}</Badge> {m.name || <span className="italic text-gray-400">(unassigned)</span>}</TableCell>
                      <TableCell className="text-right font-mono">{m.rate_percentage?.toFixed(1)}%</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(m.gross_comm)}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">-{formatCurrency(m.sst_amount)}</TableCell>
                      <TableCell className="text-right font-mono text-amber-600">-{formatCurrency(m.platform_fee)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">{formatCurrency(m.nett_comm)}</TableCell>
                    </TableRow>
                  ))}
                  {spSamChainCalc.filter(m => m.rate_percentage > 0).map((m, idx) => (
                    <TableRow key={`sp-${idx}`}>
                      {idx === 0 && <TableCell rowSpan={spSamChainCalc.filter(m => m.rate_percentage > 0).length + 3} className="font-medium text-blue-700 align-top">SP SAM</TableCell>}
                      <TableCell><Badge variant="outline" className="text-xs">{m.rank_display || m.rank?.toUpperCase()}</Badge> {m.name || <span className="italic text-gray-400">(unassigned)</span>}</TableCell>
                      <TableCell className="text-right font-mono">{m.rate_percentage?.toFixed(1)}%</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(m.gross_comm)}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">-{formatCurrency(m.sst_amount)}</TableCell>
                      <TableCell className="text-right font-mono text-amber-600">-{formatCurrency(m.platform_fee)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold">{formatCurrency(m.nett_comm)}</TableCell>
                    </TableRow>
                  ))}
                  {picCalc.gross_comm > 0 && (
                    <TableRow>
                      <TableCell><Badge variant="outline" className="text-xs bg-sky-50">PIC</Badge> {formData.pic_user_name || 'PIC'}</TableCell>
                      <TableCell className="text-right font-mono">{formData.pic_rate?.toFixed(1)}%</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(picCalc.gross_comm)}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">-{formatCurrency(picCalc.sst_amount)}</TableCell>
                      <TableCell className="text-right font-mono">&mdash;</TableCell>
                      <TableCell className="text-right font-mono font-semibold">{formatCurrency(picCalc.nett_comm)}</TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell><Badge variant="outline" className="text-xs bg-violet-50">TRIP</Badge> Trip Wallet</TableCell>
                    <TableCell className="text-right font-mono">{formData.trip_rate?.toFixed(1)}%</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(tripCalc.gross_comm)}</TableCell>
                    <TableCell className="text-right font-mono text-red-600">-{formatCurrency(tripCalc.sst_amount)}</TableCell>
                    <TableCell className="text-right font-mono">&mdash;</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{formatCurrency(tripCalc.nett_comm)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Badge variant="outline" className="text-xs bg-slate-100">SP SAM</Badge> Profit</TableCell>
                    <TableCell className="text-right font-mono">{formData.sp_sam_profit_rate?.toFixed(1)}%</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(profitCalc.gross_comm)}</TableCell>
                    <TableCell className="text-right font-mono text-green-600">NO SST</TableCell>
                    <TableCell className="text-right font-mono">&mdash;</TableCell>
                    <TableCell className="text-right font-mono font-semibold text-slate-800">{formatCurrency(profitCalc.nett_comm)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              {/* Grand Totals + Retained Profits + Fast Comm */}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-1.5 text-sm">
                  <div className="font-semibold text-slate-700 mb-2">Claim Totals</div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Gross</span>
                    <span className="font-mono">{formatCurrency(grandTotalGross)}</span>
                  </div>
                  <div className="flex justify-between text-red-600">
                    <span>Total SST (8%)</span>
                    <span className="font-mono">-{formatCurrency(grandTotalSST)}</span>
                  </div>
                  <div className="flex justify-between text-amber-600">
                    <span>Total Platform Fees</span>
                    <span className="font-mono">-{formatCurrency(srTotalPlatform + spAgentTotalPlatform)}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-blue-700 pt-1.5 border-t border-slate-200">
                    <span>Total Net Payable</span>
                    <span className="font-mono">{formatCurrency(grandTotalNett)}</span>
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 space-y-1.5 text-sm">
                  <div className="font-semibold text-emerald-800 mb-2">Company Revenue</div>
                  {isSamRumah && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">SAM Rumah Retained</span>
                      <span className="font-mono">{formatCurrency(samRumahRetained)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">SP SAM Retained</span>
                    <span className="font-mono">{formatCurrency(spSamRetained)}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-emerald-900 pt-1.5 border-t border-emerald-200">
                    <span>Total Company Revenue</span>
                    <span className="font-mono">{formatCurrency((isSamRumah ? samRumahRetained : 0) + spSamRetained)}</span>
                  </div>
                </div>
              </div>

              {isFastComm && (
                <div className="mt-3 p-3 rounded-lg bg-yellow-50 border border-yellow-300" data-testid="declaration-fast-comm-block">
                  <div className="flex items-start gap-2 mb-2">
                    <span className="text-lg">⚡</span>
                    <div className="flex-1">
                      <div className="font-semibold text-yellow-900 text-sm">Fast Comm Active — Advance at Admin Approval</div>
                      <div className="text-[11px] text-yellow-800">
                        Computed from {soldAgentSlots.length > 0
                          ? `${soldAgentSlots.length} slot(s) assigned to the closing agent`
                          : 'the NEG slot (no slot explicitly assigned)'}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <div>
                      <div className="text-gray-500">Agent Net (before fee)</div>
                      <div className="font-mono font-semibold">{formatCurrency(agentNetAfterPlatform)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Fast Comm Fee ({formData.fast_comm_fee_rate}%)</div>
                      <div className="font-mono text-red-600">-{formatCurrency(fastCommFee)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">SST on Fee (8%)</div>
                      <div className="font-mono text-red-600">-{formatCurrency(fastCommSST)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Agent Wallet Credit</div>
                      <div className="font-mono font-bold text-green-700">{formatCurrency(agentWalletAmount)}</div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Shield className="h-5 w-5" /> Declaration</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-700 leading-relaxed">
                I hereby declare that all information provided in this commission claim form is true, accurate, and complete. Any false information may result in rejection and/or disciplinary action.
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3"><Checkbox id="decl_accurate" checked={declarations.accurate} onCheckedChange={(c) => setDeclarations(prev => ({ ...prev, accurate: c }))} data-testid="decl-accurate" /><Label htmlFor="decl_accurate" className="text-sm">I declare all information is accurate and complete.</Label></div>
                <div className="flex items-center gap-3"><Checkbox id="decl_commission" checked={declarations.commission} onCheckedChange={(c) => setDeclarations(prev => ({ ...prev, commission: c }))} data-testid="decl-commission" /><Label htmlFor="decl_commission" className="text-sm">I agree to the commission distribution as outlined above.</Label></div>
                <div className="flex items-center gap-3"><Checkbox id="decl_documents" checked={declarations.documents} onCheckedChange={(c) => setDeclarations(prev => ({ ...prev, documents: c }))} data-testid="decl-documents" /><Label htmlFor="decl_documents" className="text-sm">I confirm all supporting documents are genuine and unaltered.</Label></div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-2">
                <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-800 text-sm">Approval Flow</p>
                  <p className="text-blue-700 text-xs mt-1">Agent Submit &rarr; PIC Review &rarr; Admin Review &rarr; Account Final Approval &rarr; Payment</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Navigation & Actions */}
      <div className="flex items-center justify-between pt-4 border-t">
        <div>
          {currentStep > 1 && (
            <Button variant="outline" onClick={() => setCurrentStep(prev => prev - 1)} data-testid="prev-step-btn">
              <ChevronLeft className="h-4 w-4 mr-1" /> Previous
            </Button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {onCancel && <Button variant="outline" onClick={onCancel}><X className="h-4 w-4 mr-1" /> Cancel</Button>}
          <Button variant="outline" onClick={handleSaveDraft} disabled={saving} data-testid="save-draft-btn">
            <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Save Draft')}
          </Button>
          {currentStep < 5 ? (
            <Button onClick={() => setCurrentStep(prev => prev + 1)} className="bg-blue-600 hover:bg-blue-700" data-testid="next-step-btn">
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={loading || !declarations.accurate || !declarations.commission || !declarations.documents} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-claim-btn">
              <Send className="h-4 w-4 mr-1" /> {loading ? 'Submitting...' : (isRevision ? 'Resubmit' : 'Submit for Review')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommissionClaimForm;
